import React, { useEffect, Fragment, useRef } from 'react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  Button,
  Label,
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  Input
} from 'reactstrap';
import { Loading } from './LoadingComponent';
import { CSVLink } from 'react-csv';
import Pagination from './Pagination';
import DOMPurify from '../utils/domPurifyConfig';
import { handleHtmlTagAutoComplete, checkHtmlTagAutoComplete } from '../utils/htmlTagAutoComplete';
import { handleArithmeticInput } from '../utils/arithmeticEval';
import { orderWithPinnedFirst } from '../utils/pinnedSort';
import { usePinEnabled } from '../utils/usePinEnabled';
import { useColumnWidths } from '../utils/useColumnWidths';
import { useManualResizeEnabled } from '../utils/useManualResizeEnabled';
import { handleTextareaImagePaste, selectImageTagOnDoubleClick } from '../utils/imagePasteResize';
import { renderMarkdown } from '../utils/renderMarkdown';
import ImageLightbox from './ImageLightbox';
import ColumnResizeHandle from './ColumnResizeHandle';
import { actions } from 'react-redux-form';

// Helper function to extract search keywords (excluding exclusion keywords with !)
function extractSearchKeywords(keywordString) {
  if (!keywordString) return [];

  const keywordAndArray = keywordString.split('|');
  const allKeywords = [];

  for (let k = 0; k < keywordAndArray.length; k++) {
    const keywordArray = keywordAndArray[k].split(/[\s\u3000]+/);
    for (let i = 0; i < keywordArray.length; i++) {
      const kw = keywordArray[i];
      if (kw && kw !== ' ' && kw !== '' && kw.split('')[0] !== '!') {
        // Add keyword (lowercased for multi-char, as-is for single char)
        if (kw.length === 1) {
          allKeywords.push(kw);
        } else {
          allKeywords.push(kw.toLowerCase());
        }
      }
    }
  }

  // Remove duplicates
  return [...new Set(allKeywords)];
}

// Helper function to highlight keywords in plain text
function highlightText(text, keywords) {
  if (!text || !keywords || keywords.length === 0) return text;

  // Escape special regex characters in keywords
  const escapedKeywords = keywords.map(kw =>
    kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );

  // Create regex pattern - case insensitive for multi-char keywords
  const pattern = escapedKeywords.join('|');
  const regex = new RegExp(`(${pattern})`, 'gi');

  // Split by matches and wrap matches with <mark>
  const parts = text.split(regex);

  return parts.map((part, index) => {
    const isMatch = keywords.some(kw =>
      kw.length === 1 ? part === kw : part.toLowerCase() === kw.toLowerCase()
    );
    if (isMatch) {
      return <mark key={index} style={{ backgroundColor: '#ffff00', padding: 0 }}>{part}</mark>;
    }
    return part;
  });
}

// Helper function to highlight keywords in HTML string (for dangerouslySetInnerHTML)
function highlightHTML(htmlString, keywords) {
  if (!htmlString || !keywords || keywords.length === 0) return htmlString;

  // Escape special regex characters in keywords
  const escapedKeywords = keywords.map(kw =>
    kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );

  const pattern = escapedKeywords.join('|');
  const regex = new RegExp(`(${pattern})`, 'gi');

  // Split HTML into tags and text portions
  // This regex matches HTML tags
  const tagRegex = /(<[^>]*>)/g;
  const parts = htmlString.split(tagRegex);

  // Process each part - only highlight in text portions (not tags)
  const highlighted = parts.map(part => {
    if (part.startsWith('<') && part.endsWith('>')) {
      // This is an HTML tag - don't modify
      return part;
    }
    // This is text content - highlight keywords
    return part.replace(regex, '<mark style="background-color:#ffff00;padding:0">$1</mark>');
  });

  return highlighted.join('');
}

function RenderProInputTable ({
  proInput,
  deleteProInput,
  updateProInput,
  updateProInputFields,
  resetProInputForm,
  auth,
  updatingId,
  isFullContentShown,
  toggleIsFullContentShown,
  saveScrollPosition,
  restoreScrollPosition,
  searchKeywords,
  keyword,
  view,
  getColumnWidth,
  startResize,
  htmlResumeRevision
}) {
  // Check if THIS specific row is being updated
  const isThisRowUpdating = updatingId === proInput._id;

  const [isOpen, setIsOpen] = useState(false);
  const itemColors = useSelector(state => state.fieldButtons.itemColors);
  const dispatch = useDispatch();

  // State for context menu
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0, openUpward: true });
  const menuButtonRef = useRef(null);

  // Click-outside handler to close menu
  useEffect(() => {
    if (!showContextMenu) return;
    const handleClickOutside = (event) => {
      if (!event.target.closest('.context-menu')) {
        setShowContextMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [showContextMenu]);

  // Version counter to force re-render of dangerouslySetInnerHTML content
  const [contentVersion, setContentVersion] = useState(0);

  // Optimistic UI: store pending HTML while waiting for server response
  // Using an object to track pending values for reason and condition1-20
  const [pendingFields, setPendingFields] = useState({});

  // Update version when any condition or reason changes from props
  // Also clear pending state for the changed field when server confirms
  useEffect(() => {
    setContentVersion(v => v + 1);
    // Clear all pending fields when props change (server has confirmed)
    setPendingFields({});
  }, [
    proInput.reason,
    proInput.condition1,
    proInput.condition2,
    proInput.condition3,
    proInput.condition4,
    proInput.condition5,
    proInput.condition6,
    proInput.condition7,
    proInput.condition8,
    proInput.condition9,
    proInput.condition10,
    proInput.condition11,
    proInput.condition12,
    proInput.condition13,
    proInput.condition14,
    proInput.condition15,
    proInput.condition16,
    proInput.condition17,
    proInput.condition18,
    proInput.condition19,
    proInput.condition20
  ]);

  // フォームデータをローカルstateで管理
  const [formData, setFormData] = useState({
    date: proInput.date,
    place: proInput.place,
    person: proInput.person,
    subject: proInput.subject,
    reason: proInput.reason,
    condition1: proInput.condition1,
    condition2: proInput.condition2,
    condition3: proInput.condition3,
    condition4: proInput.condition4,
    condition5: proInput.condition5,
    condition6: proInput.condition6,
    condition7: proInput.condition7,
    condition8: proInput.condition8,
    condition9: proInput.condition9,
    condition10: proInput.condition10,
    condition11: proInput.condition11,
    condition12: proInput.condition12,
    condition13: proInput.condition13,
    condition14: proInput.condition14,
    condition15: proInput.condition15,
    condition16: proInput.condition16,
    condition17: proInput.condition17,
    condition18: proInput.condition18,
    condition19: proInput.condition19,
    condition20: proInput.condition20,
    data: proInput.data,
    unit: proInput.unit
  });

  // モーダルを開く時にフォームデータをリセット
  const toggleModal = () => {
    if (!isOpen) {
      setFormData({
        date: proInput.date,
        place: proInput.place,
        person: proInput.person,
        subject: proInput.subject,
        reason: proInput.reason,
        condition1: proInput.condition1,
        condition2: proInput.condition2,
        condition3: proInput.condition3,
        condition4: proInput.condition4,
        condition5: proInput.condition5,
        condition6: proInput.condition6,
        condition7: proInput.condition7,
        condition8: proInput.condition8,
        condition9: proInput.condition9,
        condition10: proInput.condition10,
        condition11: proInput.condition11,
        condition12: proInput.condition12,
        condition13: proInput.condition13,
        condition14: proInput.condition14,
        condition15: proInput.condition15,
        condition16: proInput.condition16,
        condition17: proInput.condition17,
        condition18: proInput.condition18,
        condition19: proInput.condition19,
        condition20: proInput.condition20,
        data: proInput.data,
        unit: proInput.unit
      });
    }
    setIsOpen(!isOpen);
  };

  // フォーム入力の変更を処理
  const handleInputChange = field => e => {
    const newValue = e.target.value;
    const prevValue = formData[field] || ''; // Capture previous value

    setFormData(prev => ({
      ...prev,
      [field]: newValue
    }));

    // HTML tag auto-completion for reason and condition fields
    if (field === 'reason' || field.startsWith('condition')) {
      const input = e.target;
      const cursorPos = input.selectionStart;
      const result = checkHtmlTagAutoComplete(newValue, cursorPos, prevValue);
      if (result) {
        setFormData(prev => ({
          ...prev,
          [field]: result.newValue
        }));
        setTimeout(() => {
          input.selectionStart = result.cursorPos;
          input.selectionEnd = result.cursorPos;
          input.focus();
        }, 0);
      }

      // Arithmetic evaluation (e.g. "1+1=" -> "1+1=2")
      handleArithmeticInput(input, newValue, newVal => {
        setFormData(prev => ({
          ...prev,
          [field]: newVal
        }));
      }, prevValue);
    }
  };

  // フォーム送信を処理
  const handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();

    toggleModal();

    if (window.confirm('OK to change data?')) {
      updateProInput(
        proInput._id,
        formData.date,
        formData.place,
        formData.person,
        formData.subject,
        formData.reason,
        formData.condition1,
        formData.condition2,
        formData.condition3,
        formData.condition4,
        formData.condition5,
        formData.condition6,
        formData.condition7,
        formData.condition8,
        formData.condition9,
        formData.condition10,
        formData.condition11,
        formData.condition12,
        formData.condition13,
        formData.condition14,
        formData.condition15,
        formData.condition16,
        formData.condition17,
        formData.condition18,
        formData.condition19,
        formData.condition20,
        formData.data,
        formData.unit
      );
    }
  };

  // Handle menu button click
  const handleMenuButtonClick = (event) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    // The menu's content only ever takes one of two shapes (owner-only
    // actions are shown or not), so its height is known up front — no need
    // to measure it after the fact, which would reposition it visibly.
    // It normally opens upward (anchored above the button); if there isn't
    // enough room above the header, open it downward instead.
    const isOwner = auth.id === proInput.user;
    const menuHeight = isOwner ? 220 : 100;
    const headerEl = document.querySelector('.header');
    const safeTop = headerEl ? headerEl.getBoundingClientRect().bottom : 0;
    const openUpward = rect.top - safeTop >= menuHeight;
    // Anchor with `bottom` (not a translateY transform) when opening upward,
    // so the entrance animation's `transform: scale(...)` doesn't clobber a
    // separate positioning transform and cause a visible jump once it ends.
    setMenuPosition({
      x: rect.left,
      top: openUpward ? undefined : rect.bottom,
      bottom: openUpward ? window.innerHeight - rect.top : undefined,
      openUpward
    });
    setShowContextMenu(true);
  };

  // 行クリック処理 - 背景色切り替え（複数選択可能）
  const handleClick = (event, id) => {
    try {
      // Let interactive descendants (details, buttons, inputs …) behave normally
      if (event.target.closest('details,button,input,select,textarea,a,.context-menu')) return;

      // Toggle background color (allows multiple selections)
      const newColor = itemColors[id] === '#d6eaf8' ? 'white' : '#d6eaf8';

      dispatch({
        type: 'SET_ITEM_COLOR',
        payload: { id, color: newColor }
      });

      if (newColor === '#d6eaf8') {
        // Selecting: Update browser URL
        const keywordParam = keyword ? `&keyword=${encodeURIComponent(keyword)}` : '';
        const viewParam = `&view=${view ? 'list' : 'spreadsheet'}`;
        const linkPath = `/proInputs?id=${id}${keywordParam}${viewParam}`;
        window.history.pushState({}, '', linkPath);

        // Copy row data to proInput form using react-redux-form actions.
        // Date is deliberately left blank: duplicating a row almost always
        // means entering a new date, so leaving the old one in just adds an
        // extra "clear the date" step every time.
        dispatch(actions.change('proInput.date', ''));
        dispatch(actions.change('proInput.place', proInput.place));
        dispatch(actions.change('proInput.person', proInput.person));
        dispatch(actions.change('proInput.subject', proInput.subject));
        dispatch(actions.change('proInput.reason', proInput.reason));
        dispatch(actions.change('proInput.condition1', proInput.condition1));
        dispatch(actions.change('proInput.condition2', proInput.condition2));
        dispatch(actions.change('proInput.condition3', proInput.condition3));
        dispatch(actions.change('proInput.condition4', proInput.condition4));
        dispatch(actions.change('proInput.condition5', proInput.condition5));
        dispatch(actions.change('proInput.condition6', proInput.condition6));
        dispatch(actions.change('proInput.condition7', proInput.condition7));
        dispatch(actions.change('proInput.condition8', proInput.condition8));
        dispatch(actions.change('proInput.condition9', proInput.condition9));
        dispatch(actions.change('proInput.condition10', proInput.condition10));
        dispatch(actions.change('proInput.condition11', proInput.condition11));
        dispatch(actions.change('proInput.condition12', proInput.condition12));
        dispatch(actions.change('proInput.condition13', proInput.condition13));
        dispatch(actions.change('proInput.condition14', proInput.condition14));
        dispatch(actions.change('proInput.condition15', proInput.condition15));
        dispatch(actions.change('proInput.condition16', proInput.condition16));
        dispatch(actions.change('proInput.condition17', proInput.condition17));
        dispatch(actions.change('proInput.condition18', proInput.condition18));
        dispatch(actions.change('proInput.condition19', proInput.condition19));
        dispatch(actions.change('proInput.condition20', proInput.condition20));
        dispatch(actions.change('proInput.data', proInput.data));
        dispatch(actions.change('proInput.unit', proInput.unit));

        // Save to localStorage (date left blank - see comment above)
        const formData = {
          date: '',
          place: proInput.place || '',
          person: proInput.person || '',
          subject: proInput.subject || '',
          reason: proInput.reason || '',
          condition1: proInput.condition1 || '',
          condition2: proInput.condition2 || '',
          condition3: proInput.condition3 || '',
          condition4: proInput.condition4 || '',
          condition5: proInput.condition5 || '',
          condition6: proInput.condition6 || '',
          condition7: proInput.condition7 || '',
          condition8: proInput.condition8 || '',
          condition9: proInput.condition9 || '',
          condition10: proInput.condition10 || '',
          condition11: proInput.condition11 || '',
          condition12: proInput.condition12 || '',
          condition13: proInput.condition13 || '',
          condition14: proInput.condition14 || '',
          condition15: proInput.condition15 || '',
          condition16: proInput.condition16 || '',
          condition17: proInput.condition17 || '',
          condition18: proInput.condition18 || '',
          condition19: proInput.condition19 || '',
          condition20: proInput.condition20 || '',
          data: proInput.data || '',
          unit: proInput.unit || ''
        };
        localStorage.setItem('proDataForm', JSON.stringify(formData));

        // Dispatch custom event to update X button visibility in ProComponent
        try {
          window.dispatchEvent(new CustomEvent('proFormRowSelected', { detail: { formData } }));
        } catch (eventErr) {
          console.error('Failed to dispatch proFormRowSelected event:', eventErr);
        }
      } else {
        // Deselecting: Clear the proInput form
        dispatch(actions.change('proInput.date', ''));
        dispatch(actions.change('proInput.place', ''));
        dispatch(actions.change('proInput.person', ''));
        dispatch(actions.change('proInput.subject', ''));
        dispatch(actions.change('proInput.reason', ''));
        dispatch(actions.change('proInput.condition1', ''));
        dispatch(actions.change('proInput.condition2', ''));
        dispatch(actions.change('proInput.condition3', ''));
        dispatch(actions.change('proInput.condition4', ''));
        dispatch(actions.change('proInput.condition5', ''));
        dispatch(actions.change('proInput.condition6', ''));
        dispatch(actions.change('proInput.condition7', ''));
        dispatch(actions.change('proInput.condition8', ''));
        dispatch(actions.change('proInput.condition9', ''));
        dispatch(actions.change('proInput.condition10', ''));
        dispatch(actions.change('proInput.condition11', ''));
        dispatch(actions.change('proInput.condition12', ''));
        dispatch(actions.change('proInput.condition13', ''));
        dispatch(actions.change('proInput.condition14', ''));
        dispatch(actions.change('proInput.condition15', ''));
        dispatch(actions.change('proInput.condition16', ''));
        dispatch(actions.change('proInput.condition17', ''));
        dispatch(actions.change('proInput.condition18', ''));
        dispatch(actions.change('proInput.condition19', ''));
        dispatch(actions.change('proInput.condition20', ''));
        dispatch(actions.change('proInput.data', ''));
        dispatch(actions.change('proInput.unit', ''));

        // Clear localStorage
        localStorage.removeItem('proDataForm');

        // Dispatch custom event to clear X button visibility in ProComponent
        const emptyFormData = {
          date: '', place: '', person: '', subject: '', reason: '',
          condition1: '', condition2: '', condition3: '', condition4: '', condition5: '',
          condition6: '', condition7: '', condition8: '', condition9: '', condition10: '',
          condition11: '', condition12: '', condition13: '', condition14: '', condition15: '',
          condition16: '', condition17: '', condition18: '', condition19: '', condition20: '',
          data: '', unit: ''
        };
        try {
          window.dispatchEvent(new CustomEvent('proFormRowSelected', { detail: { formData: emptyFormData } }));
        } catch (eventErr) {
          console.error('Failed to dispatch proFormRowSelected event:', eventErr);
        }
      }
    } catch (err) {
      console.error('Error in handleClick:', err);
    }
  };

  // Context menu actions
  const handleMenuEdit = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setShowContextMenu(false);
    toggleModal();
  };

  const handleMenuDelete = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setShowContextMenu(false);
    if (window.confirm('Are you sure?')) {
      deleteProInput(proInput._id);
    }
  };

  const handleMenuCopyLink = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setShowContextMenu(false);
    const keywordParam = keyword ? `&keyword=${encodeURIComponent(keyword)}` : '';
    const viewParam = `&view=${view ? 'list' : 'spreadsheet'}`;
    const linkPath = `/proInputs?id=${proInput._id}${keywordParam}${viewParam}`;
    const fullUrl = window.location.origin + linkPath;
    try {
      await navigator.clipboard.writeText(fullUrl);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleMenuTogglePin = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setShowContextMenu(false);
    updateProInputFields(proInput._id, { pinned: !proInput.pinned });
  };

  const handleLinkClick = event => {
    event.stopPropagation();
  };

  const [lightboxImage, setLightboxImage] = useState(null);

  const handleImageClick = e => {
    if (e.target.tagName === 'IMG') {
      e.preventDefault();
      e.stopPropagation();
      setLightboxImage({
        src: e.target.src,
        width: e.target.offsetWidth,
        height: e.target.offsetHeight
      });
    }
  };

  const [isDateEditing, setIsDateEditing] = useState(false);
  const [isPlaceEditing, setIsPlaceEditing] = useState(false);
  const [isPersonEditing, setIsPersonEditing] = useState(false);
  const [isSubjectEditing, setIsSubjectEditing] = useState(false);
  const [isReasonEditing, setIsReasonEditing] = useState(false);
  const [isCondition1Editing, setIsCondition1Editing] = useState(false);
  const [isCondition2Editing, setIsCondition2Editing] = useState(false);
  const [isCondition3Editing, setIsCondition3Editing] = useState(false);
  const [isCondition4Editing, setIsCondition4Editing] = useState(false);
  const [isCondition5Editing, setIsCondition5Editing] = useState(false);
  const [isCondition6Editing, setIsCondition6Editing] = useState(false);
  const [isCondition7Editing, setIsCondition7Editing] = useState(false);
  const [isCondition8Editing, setIsCondition8Editing] = useState(false);
  const [isCondition9Editing, setIsCondition9Editing] = useState(false);
  const [isCondition10Editing, setIsCondition10Editing] = useState(false);
  const [isCondition11Editing, setIsCondition11Editing] = useState(false);
  const [isCondition12Editing, setIsCondition12Editing] = useState(false);
  const [isCondition13Editing, setIsCondition13Editing] = useState(false);
  const [isCondition14Editing, setIsCondition14Editing] = useState(false);
  const [isCondition15Editing, setIsCondition15Editing] = useState(false);
  const [isCondition16Editing, setIsCondition16Editing] = useState(false);
  const [isCondition17Editing, setIsCondition17Editing] = useState(false);
  const [isCondition18Editing, setIsCondition18Editing] = useState(false);
  const [isCondition19Editing, setIsCondition19Editing] = useState(false);
  const [isCondition20Editing, setIsCondition20Editing] = useState(false);
  const [isDataEditing, setIsDataEditing] = useState(false);
  const [isUnitEditing, setIsUnitEditing] = useState(false);

  const [tempValue, setTempValue] = useState('');

  const handleDoubleClick = e => {
    const fieldName = e.target.getAttribute('name');
    const fieldValue = proInput[fieldName];
    setTempValue(fieldValue); // Store the current value before editing
    if (fieldName === 'date') setIsDateEditing(true);
    if (fieldName === 'place') setIsPlaceEditing(true);
    if (fieldName === 'person') setIsPersonEditing(true);
    if (fieldName === 'subject') setIsSubjectEditing(true);
    if (fieldName === 'reason') setIsReasonEditing(true);
    if (fieldName === 'condition1') setIsCondition1Editing(true);
    if (fieldName === 'condition2') setIsCondition2Editing(true);
    if (fieldName === 'condition3') setIsCondition3Editing(true);
    if (fieldName === 'condition4') setIsCondition4Editing(true);
    if (fieldName === 'condition5') setIsCondition5Editing(true);
    if (fieldName === 'condition6') setIsCondition6Editing(true);
    if (fieldName === 'condition7') setIsCondition7Editing(true);
    if (fieldName === 'condition8') setIsCondition8Editing(true);
    if (fieldName === 'condition9') setIsCondition9Editing(true);
    if (fieldName === 'condition10') setIsCondition10Editing(true);
    if (fieldName === 'condition11') setIsCondition11Editing(true);
    if (fieldName === 'condition12') setIsCondition12Editing(true);
    if (fieldName === 'condition13') setIsCondition13Editing(true);
    if (fieldName === 'condition14') setIsCondition14Editing(true);
    if (fieldName === 'condition15') setIsCondition15Editing(true);
    if (fieldName === 'condition16') setIsCondition16Editing(true);
    if (fieldName === 'condition17') setIsCondition17Editing(true);
    if (fieldName === 'condition18') setIsCondition18Editing(true);
    if (fieldName === 'condition19') setIsCondition19Editing(true);
    if (fieldName === 'condition20') setIsCondition20Editing(true);
    if (fieldName === 'data') setIsDataEditing(true);
    if (fieldName === 'unit') setIsUnitEditing(true);
  };

  const handleChange = e => {
    const newValue = e.target.value;
    const prevValue = tempValue; // Capture previous value before update
    setTempValue(newValue); // Update the temporary value on each key press

    // textareaの高さを自動調整（reason/conditionフィールド用）
    if (e.target.tagName === 'TEXTAREA') {
      e.target.style.height = 'auto';
      e.target.style.height = e.target.scrollHeight + 'px';
    }

    // HTML tag auto-completion for reason/condition fields (works for both textarea and input)
    const fieldName = e.target.name;
    if (fieldName === 'reason' || fieldName.startsWith('condition')) {
      handleHtmlTagAutoComplete(e.target, newValue, setTempValue, prevValue);
      handleArithmeticInput(e.target, newValue, setTempValue, prevValue);
    }
  };
  const handleBlur = e => {
    if (!document.hasFocus()) {
      return;
    }
    if (
      !(
        isDateEditing ||
        isPlaceEditing ||
        isPersonEditing ||
        isSubjectEditing ||
        isReasonEditing ||
        isCondition1Editing ||
        isCondition2Editing ||
        isCondition3Editing ||
        isCondition4Editing ||
        isCondition5Editing ||
        isCondition6Editing ||
        isCondition7Editing ||
        isCondition8Editing ||
        isCondition9Editing ||
        isCondition10Editing ||
        isCondition11Editing ||
        isCondition12Editing ||
        isCondition13Editing ||
        isCondition14Editing ||
        isCondition15Editing ||
        isCondition16Editing ||
        isCondition17Editing ||
        isCondition18Editing ||
        isCondition19Editing ||
        isCondition20Editing ||
        isDataEditing ||
        isUnitEditing
      )
    ) {
      return;
    }
    const fieldName = e.target.name;

    if (tempValue !== proInput[fieldName]) {
      // For all fields, use the tempValue directly
      // DON'T sync embedded form elements when editing the main field

      isConfirmingRef.current = true;
      const confirmed = window.confirm('OK to change data?');
      // Reset after a short delay to allow queued timer callbacks to check this flag
      setTimeout(() => {
        isConfirmingRef.current = false;
      }, 150);

      if (confirmed) {
        if (saveScrollPosition) {
          saveScrollPosition();
        }

        updateProInputFields(proInput._id, { [fieldName]: tempValue }).then(() => {
          if (restoreScrollPosition) {
            restoreScrollPosition();
          }
        });
      }
    }

    if (fieldName === 'date') setIsDateEditing(false);
    if (fieldName === 'place') setIsPlaceEditing(false);
    if (fieldName === 'person') setIsPersonEditing(false);
    if (fieldName === 'subject') setIsSubjectEditing(false);
    if (fieldName === 'reason') setIsReasonEditing(false);
    if (fieldName === 'condition1') setIsCondition1Editing(false);
    if (fieldName === 'condition2') setIsCondition2Editing(false);
    if (fieldName === 'condition3') setIsCondition3Editing(false);
    if (fieldName === 'condition4') setIsCondition4Editing(false);
    if (fieldName === 'condition5') setIsCondition5Editing(false);
    if (fieldName === 'condition6') setIsCondition6Editing(false);
    if (fieldName === 'condition7') setIsCondition7Editing(false);
    if (fieldName === 'condition8') setIsCondition8Editing(false);
    if (fieldName === 'condition9') setIsCondition9Editing(false);
    if (fieldName === 'condition10') setIsCondition10Editing(false);
    if (fieldName === 'condition11') setIsCondition11Editing(false);
    if (fieldName === 'condition12') setIsCondition12Editing(false);
    if (fieldName === 'condition13') setIsCondition13Editing(false);
    if (fieldName === 'condition14') setIsCondition14Editing(false);
    if (fieldName === 'condition15') setIsCondition15Editing(false);
    if (fieldName === 'condition16') setIsCondition16Editing(false);
    if (fieldName === 'condition17') setIsCondition17Editing(false);
    if (fieldName === 'condition18') setIsCondition18Editing(false);
    if (fieldName === 'condition19') setIsCondition19Editing(false);
    if (fieldName === 'condition20') setIsCondition20Editing(false);
    if (fieldName === 'data') setIsDataEditing(false);
    if (fieldName === 'unit') setIsUnitEditing(false);
  };

  // const handleKeyDown = event => {
  //   if (event.key === 'Enter') {
  //     event.target.blur();
  //   }
  // };

  const handleKeyDown = event => {
    // Only single-line fields (date, place, ...) need Enter blocked; reason
    // edits as a <textarea>, where Enter is supposed to insert a newline.
    // This listener lives on the <td> and catches Enter bubbling up from
    // whichever field is focused inside it, so without this check it
    // silently ate every newline typed into reason, collapsing multi-line
    // content (e.g. a LaTeX $$...$$ block, which needs real line breaks
    // around the delimiters) onto a single line. condition1-20 edit as a
    // single-line <Input> and were never able to hold real newlines anyway.
    if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
      event.preventDefault();
    }
  };

  const isNaNStyle = isNaN(proInput.data.replace(/,/g, ''))
    ? {}
    : { textAlign: 'right' };
  const backgroundStyle = {
    backgroundColor: itemColors[proInput._id] || 'white'
  }; // Example new style
  const dataStyle = { ...isNaNStyle, ...backgroundStyle };

  const containerRef = useRef(null);
  const pendingCellRef = useRef(null); // Track the cell being edited
  const originalHTMLRef = useRef(null); // Store original HTML for cancel
  const isConfirmingRef = useRef(false);

  // Handler for "click outside cell" to trigger confirmation
  useEffect(() => {
    // Helper to start tracking a cell for editing
    const startTrackingCell = (td, field, proInputId) => {
      if (!pendingCellRef.current || pendingCellRef.current.td !== td) {
        // [name] targets the actual rendered content div, not the width
        // wrapper div the resize feature adds around reason/condition1-20.
        const div = td.querySelector('div[name]');
        originalHTMLRef.current = div ? div.innerHTML : td.innerHTML;
        pendingCellRef.current = { td, field, proInputId };
      }
    };

    const serializeCellValue = (td, field) => {
      const div = td.querySelector('div[name]');
      const container = div || td;
      container.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(control => {
        if (control.checked) control.setAttribute('checked', 'checked');
        else control.removeAttribute('checked');
      });
      container.querySelectorAll(
        'input[type="text"], input[type="number"], input[type="email"], input[type="url"], input[type="tel"], input[type="date"], input[type="time"], input[type="datetime-local"]'
      ).forEach(control => control.setAttribute('value', control.value));
      container.querySelectorAll('select').forEach(select => {
        select.querySelectorAll('option').forEach(option => {
          if (option.value === select.value) option.setAttribute('selected', 'selected');
          else option.removeAttribute('selected');
        });
      });
      container.querySelectorAll('textarea').forEach(textarea => {
        const isMainEditor = textarea.name === field &&
          (field === 'reason' ? isReasonEditing : conditionEditingByField[field]);
        if (!isMainEditor) textarea.textContent = textarea.value;
      });
      return div ? div.innerHTML : td.innerHTML;
    };

    // Is `field`'s own dedicated edit control (the reason <textarea> or a
    // condition's <Input>) the thing that's currently focused/changing?
    // If so, this is the ordinary double-click-to-edit flow, already fully
    // handled by isReasonEditing/isConditionNEditing + handleBlur - it must
    // NOT also be picked up here as if it were a stray embedded-checkbox
    // edit, or the two independent "confirm this change?" flows race each
    // other and can each pop their own dialog for the same edit.
    const conditionEditingByField = {
      condition1: isCondition1Editing,
      condition2: isCondition2Editing,
      condition3: isCondition3Editing,
      condition4: isCondition4Editing,
      condition5: isCondition5Editing,
      condition6: isCondition6Editing,
      condition7: isCondition7Editing,
      condition8: isCondition8Editing,
      condition9: isCondition9Editing,
      condition10: isCondition10Editing,
      condition11: isCondition11Editing,
      condition12: isCondition12Editing,
      condition13: isCondition13Editing,
      condition14: isCondition14Editing,
      condition15: isCondition15Editing,
      condition16: isCondition16Editing,
      condition17: isCondition17Editing,
      condition18: isCondition18Editing,
      condition19: isCondition19Editing,
      condition20: isCondition20Editing
    };
    const isActivelyEditingThisField = (field, targetName) => {
      if (targetName !== field) return false;
      if (field === 'reason') return isReasonEditing;
      return !!conditionEditingByField[field];
    };

    // Double-click on time/date inputs to clear their value
    // (iOS Safari's "reset" only resets to the HTML attribute value, not to empty)
    const handleDoubleClickInput = e => {
      const target = e.target;
      if (target.tagName !== 'INPUT') return;

      const inputType = target.type;
      if (inputType !== 'time' && inputType !== 'date' && inputType !== 'datetime-local') return;

      // Only offer to clear if the input has a value
      if (!target.value) return;

      const td = target.closest('td[data-cellid]');
      if (!td) return;

      const cellId = td.getAttribute('data-cellid');
      const [proInputId, field] = cellId.split('|');

      const isConditionField = /^condition([1-9]|1[0-9]|20)$/.test(field);
      if (field !== 'reason' && !isConditionField) return;
      if (proInputId !== proInput._id) return;

      e.preventDefault();
      e.stopPropagation();

      if (window.confirm('Clear this value?')) {
        target.value = '';
        target.setAttribute('value', '');
        // Start tracking to capture the change
        startTrackingCell(td, field, proInputId);
      }
    };

    // Track when user starts editing a cell (focus on any form element)
    const handleFocusIn = e => {
      if (
        !e.target ||
        !(
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'SELECT' ||
          e.target.tagName === 'TEXTAREA'
        )
      ) {
        return;
      }

      const td = e.target.closest('td[data-cellid]');
      if (!td) return;

      const cellId = td.getAttribute('data-cellid');
      const [proInputId, field] = cellId.split('|');

      // Only handle reason and condition1-20 fields
      const isConditionField = /^condition([1-9]|1[0-9]|20)$/.test(field);
      if (field !== 'reason' && !isConditionField) return;
      if (proInputId !== proInput._id) return;

      // Skip if we're editing this field's own main control - already fully
      // handled by isReasonEditing/isConditionNEditing + handleBlur (see
      // isActivelyEditingThisField above for why this must not use eval()).
      if (isActivelyEditingThisField(field, e.target.name)) {
        return;
      }

      startTrackingCell(td, field, proInputId);
    };

    // Also track changes on checkboxes/radios/selects (they may not trigger focusin on iOS)
    const handleChange = e => {
      if (
        !e.target ||
        !(
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'SELECT' ||
          e.target.tagName === 'TEXTAREA'
        )
      ) {
        return;
      }

      const td = e.target.closest('td[data-cellid]');
      if (!td) return;

      const cellId = td.getAttribute('data-cellid');
      const [proInputId, field] = cellId.split('|');

      // Only handle reason and condition1-20 fields
      const isConditionField = /^condition([1-9]|1[0-9]|20)$/.test(field);
      if (field !== 'reason' && !isConditionField) return;
      if (proInputId !== proInput._id) return;

      // A condition field's own edit control is an <Input> (an <input>
      // tag), which - unlike reason's <textarea> - matches this listener's
      // own tag filter above. Without this, typing a change into it fires
      // this exact same "track this as an embedded-element edit" path in
      // parallel with the real edit-and-blur flow already handling it, and
      // both can independently ask "OK to change data?" for the same edit.
      if (isActivelyEditingThisField(field, e.target.name)) {
        return;
      }

      // Keep the edit pending so checkbox/radio/select changes follow the
      // same click-away confirmation flow as text and time inputs.
      startTrackingCell(td, field, proInputId);
    };

    // Track input events (fires more reliably than change, especially for time/date inputs on iOS)
    const handleInput = e => {
      if (!e.target || !['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;

      const td = e.target.closest('td[data-cellid]');
      if (!td) return;

      const cellId = td.getAttribute('data-cellid');
      const [proInputId, field] = cellId.split('|');

      const isConditionField = /^condition([1-9]|1[0-9]|20)$/.test(field);
      if (field !== 'reason' && !isConditionField) return;
      if (proInputId !== proInput._id) return;

      // Same reasoning as handleChange above - a condition field's edit
      // control is an <input> tag, matching this listener's filter, unlike
      // reason's <textarea>.
      if (isActivelyEditingThisField(field, e.target.name)) {
        return;
      }

      startTrackingCell(td, field, proInputId);
      // Don't update an input's `value` attribute while it is being edited:
      // iOS uses that attribute as the date/time picker's Reset baseline. Sync
      // the live value into HTML only when the edit is committed.
      if (e.target.tagName === 'TEXTAREA' && e.target.name !== field) {
        e.target.textContent = e.target.value;
      }
    };

    // Commit pending embedded-control edits when the tab is suspended or
    // closed. The normal click-outside path still asks for confirmation.
    const flushPendingEdit = event => {
      if (event && event.type === 'visibilitychange' && !document.hidden) return;
      const pending = pendingCellRef.current;
      if (!pending || !pending.td.isConnected) return;

      const { field, proInputId } = pending;
      const newValue = serializeCellValue(pending.td, field);
      if (newValue === originalHTMLRef.current) return;

      pendingCellRef.current = null;
      originalHTMLRef.current = null;
      updateProInputFields(proInputId, { [field]: newValue });
    };

    // Handle click outside to confirm changes
    const handleClickOutside = e => {
      if (isConfirmingRef.current) return;
      if (!pendingCellRef.current) return;

      // A column-resize drag isn't "clicking outside" the cell being edited -
      // it's a capture-phase listener so it'd otherwise fire (and pop the
      // confirm() dialog below) before the resize handle's own handler runs.
      if (e.target.closest && e.target.closest('.column-resize-handle')) return;

      // Skip if we're in direct edit mode (double-click editing)
      // The handleBlur will handle saving in that case
      const isDirectEditing = isReasonEditing ||
        isCondition1Editing || isCondition2Editing || isCondition3Editing ||
        isCondition4Editing || isCondition5Editing || isCondition6Editing ||
        isCondition7Editing || isCondition8Editing || isCondition9Editing ||
        isCondition10Editing || isCondition11Editing || isCondition12Editing ||
        isCondition13Editing || isCondition14Editing || isCondition15Editing ||
        isCondition16Editing || isCondition17Editing || isCondition18Editing ||
        isCondition19Editing || isCondition20Editing;

      if (isDirectEditing) {
        pendingCellRef.current = null;
        originalHTMLRef.current = null;
        return;
      }

      const pending = pendingCellRef.current;
      const { td, field, proInputId } = pending;

      // The cell's DOM node can be gone (re-render, pagination, search) while
      // this ref is still holding on to it - without this, every future
      // click anywhere on the page (e.g. the hamburger menu button) would
      // keep misfiring into the "confirm changes" flow below for a cell that
      // no longer exists, since a detached node can never "contain" the new
      // click target.
      if (!td.isConnected) {
        pendingCellRef.current = null;
        originalHTMLRef.current = null;
        return;
      }

      // Check if click is inside the same cell
      if (td.contains(e.target)) {
        return; // Still inside the cell, don't confirm yet
      }

      // Everything below reads the embedded field's current value, so it has
      // to wait until the browser's native blur for this tap has actually
      // happened - this mousedown/touchstart capture-phase listener runs
      // BEFORE that (confirmed: activeElement is still the old field here,
      // and its blur only fires once this event finishes dispatching), so
      // reading .value synchronously here can catch an in-progress IME
      // composition (e.g. Japanese kana-to-kanji conversion, but this can
      // also just be a plain <input type="text">) before the browser commits
      // its last, uncommitted characters into the value on blur - silently
      // dropping exactly the most recently typed content. Deferring with
      // setTimeout(0) runs after that commit instead.
      setTimeout(() => {
        // Something else may have already claimed or cleared this exact
        // pending cell while we were waiting (e.g. focus moved straight to
        // another tracked field) - if so, leave it alone.
        if (pendingCellRef.current !== pending) return;

        // Sync and confirm.
        // [name] targets the actual rendered content div, not the width
        // wrapper div the resize feature adds around reason/condition1-20.
        const div = td.querySelector('div[name]');
        const container = div || td;

        // Sync all form elements to their HTML attributes
        container.querySelectorAll('input[type="checkbox"]').forEach(cb => {
          if (cb.checked) {
            cb.setAttribute('checked', 'checked');
          } else {
            cb.removeAttribute('checked');
          }
        });

        container.querySelectorAll('input[type="radio"]').forEach(radio => {
          if (radio.checked) {
            radio.setAttribute('checked', 'checked');
          } else {
            radio.removeAttribute('checked');
          }
        });

        container.querySelectorAll('select').forEach(sel => {
          sel.querySelectorAll('option').forEach(opt => {
            if (opt.value === sel.value) {
              opt.setAttribute('selected', 'selected');
            } else {
              opt.removeAttribute('selected');
            }
          });
        });

        container
          .querySelectorAll(
            'input[type="text"], input[type="number"], input[type="email"], input[type="url"], input[type="tel"], input[type="date"], input[type="time"], input[type="datetime-local"]'
          )
          .forEach(inp => {
            inp.setAttribute('value', inp.value);
          });

        container.querySelectorAll('textarea').forEach(textarea => {
          if (!textarea.hasAttribute('name')) {
            textarea.textContent = textarea.value;
          }
        });

        // Get the updated HTML
        const newValue = div ? div.innerHTML : td.innerHTML;

        // Check if anything actually changed
        if (newValue === originalHTMLRef.current) {
          // No changes, just clear the pending state
          pendingCellRef.current = null;
          originalHTMLRef.current = null;
          return;
        }


        isConfirmingRef.current = true;

        if (window.confirm('OK to change data?')) {
          // Optimistic UI: immediately show the new value while waiting for server
          setPendingFields(prev => ({ ...prev, [field]: newValue }));

          updateProInputFields(proInputId, { [field]: newValue });
        } else {
          // User canceled - restore original HTML
          if (div) {
            div.innerHTML = originalHTMLRef.current;
          } else {
            td.innerHTML = originalHTMLRef.current;
          }
        }

        isConfirmingRef.current = false;
        pendingCellRef.current = null;
        originalHTMLRef.current = null;
      }, 0);
    };

    const rowContainer = containerRef.current;
    if (rowContainer) {
      rowContainer.addEventListener('focusin', handleFocusIn, true);
      // Also listen for change events to track checkbox/radio/select changes on iOS
      rowContainer.addEventListener('change', handleChange, true);
      // Listen for input events (fires more reliably for time/date inputs, especially on iOS reset)
      rowContainer.addEventListener('input', handleInput, true);
      // Double-click on time/date inputs to clear them
      rowContainer.addEventListener('dblclick', handleDoubleClickInput, true);
    }

    // Listen for clicks/touches on the entire document to detect "click outside"
    // Use both mousedown and touchstart for cross-platform support
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);
    window.addEventListener('pagehide', flushPendingEdit);
    document.addEventListener('visibilitychange', flushPendingEdit);

    return () => {
      if (rowContainer) {
        rowContainer.removeEventListener('focusin', handleFocusIn, true);
        rowContainer.removeEventListener('change', handleChange, true);
        rowContainer.removeEventListener('input', handleInput, true);
        rowContainer.removeEventListener('dblclick', handleDoubleClickInput, true);
      }
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
      window.removeEventListener('pagehide', flushPendingEdit);
      document.removeEventListener('visibilitychange', flushPendingEdit);
    };
  }, [
    proInput,
    updateProInput,
    updateProInputFields,
    isReasonEditing,
    isCondition1Editing,
    isCondition2Editing,
    isCondition3Editing,
    isCondition4Editing,
    isCondition5Editing,
    isCondition6Editing,
    isCondition7Editing,
    isCondition8Editing,
    isCondition9Editing,
    isCondition10Editing,
    isCondition11Editing,
    isCondition12Editing,
    isCondition13Editing,
    isCondition14Editing,
    isCondition15Editing,
    isCondition16Editing,
    isCondition17Editing,
    isCondition18Editing,
    isCondition19Editing,
    isCondition20Editing
  ]);

  return (
    <tr
      ref={containerRef}
      data-row-id={proInput._id}
      style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
    >
      <ImageLightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      {/* Context Menu - rendered via portal to avoid table layout issues */}
      {showContextMenu && createPortal(
        <div
          className="context-menu"
          style={{
            position: 'fixed',
            left: menuPosition.x,
            top: menuPosition.top,
            bottom: menuPosition.bottom,
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            zIndex: 1000,
            fontSize: '13px',
            userSelect: 'none',
            minWidth: '180px',
            overflow: 'hidden',
            animation: 'menuScaleIn 150ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            transformOrigin: menuPosition.openUpward ? 'bottom left' : 'top left'
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          <style>{`
            @keyframes menuScaleIn {
              from { opacity: 0; transform: scale(0.9); }
              to { opacity: 1; transform: scale(1); }
            }
            .context-menu-item:hover {
              background-color: #f0f0f0;
            }
            .context-menu-item-danger:hover {
              background-color: rgba(163, 45, 45, 0.08);
            }
          `}</style>
          {/* Header */}
          <div style={{
            padding: '8px 12px',
            borderBottom: '1px solid #e8e8e8',
            backgroundColor: '#f8f9fa',
            fontSize: '11px',
            color: '#666',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {proInput.subject ? (proInput.subject.length > 25 ? proInput.subject.substring(0, 25) + '...' : proInput.subject) : 'Record'}
          </div>

          {/* Safe Actions */}
          {auth.id === proInput.user && (
            <div
              className="context-menu-item"
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'background-color 100ms'
              }}
              onClick={() => { handleMenuEdit(); setShowContextMenu(false); }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit record
              </span>
              <span style={{
                fontSize: '10px',
                fontFamily: 'monospace',
                backgroundColor: '#e8e8e8',
                padding: '2px 6px',
                borderRadius: '3px',
                color: '#666'
              }}>E</span>
            </div>
          )}
          <div
            className="context-menu-item"
            style={{
              padding: '10px 12px',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              transition: 'background-color 100ms'
            }}
            onClick={() => { handleMenuCopyLink(); setShowContextMenu(false); }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              Copy link
            </span>
            <span style={{
              fontSize: '10px',
              fontFamily: 'monospace',
              backgroundColor: '#e8e8e8',
              padding: '2px 6px',
              borderRadius: '3px',
              color: '#666'
            }}>C</span>
          </div>
          {auth.id === proInput.user && (
            <div
              className="context-menu-item"
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'background-color 100ms'
              }}
              onClick={() => { handleMenuTogglePin(); setShowContextMenu(false); }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l1.5 5.5L19 9l-5.5 1.5L12 16l-1.5-5.5L5 9l5.5-1.5L12 2z" />
                  <line x1="12" y1="16" x2="12" y2="22" />
                </svg>
                {proInput.pinned ? 'Unpin' : 'Pin to top'}
              </span>
            </div>
          )}

          {/* Divider + Danger Zone */}
          {auth.id === proInput.user && (
            <>
              <div style={{ height: '1px', backgroundColor: '#e8e8e8', margin: '4px 0' }} />
              <div
                className="context-menu-item-danger"
                style={{
                  padding: '10px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  color: '#A32D2D',
                  transition: 'background-color 100ms'
                }}
                onClick={() => { handleMenuDelete(); setShowContextMenu(false); }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Delete
                </span>
                <span style={{
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  backgroundColor: 'rgba(163, 45, 45, 0.12)',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  color: '#A32D2D'
                }}>D</span>
              </div>
            </>
          )}
        </div>,
        document.body
      )}
      <Modal
        isOpen={isOpen}
        toggle={toggleModal}
        size='lg'
        style={{ maxWidth: '1000px', width: '100%' }}
        className='my-modal'
      >
        <ModalHeader
          style={{
            borderBottom: '1px solid #c5d8ec',
            padding: '12px 16px'
          }}
          close={
            <button
              type="button"
              onClick={toggleModal}
              style={{
                width: '26px',
                height: '26px',
                borderRadius: '6px',
                background: 'rgba(255,255,255,0.6)',
                border: 'none',
                color: '#4a6a8a',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              ×
            </button>
          }
        >
          <span style={{ fontWeight: 500, fontSize: '15px' }}>Update ProInput</span>
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit} onClick={e => e.stopPropagation()}>
            <Row className='form-group mb-3'>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  id={`edit-pro-date-${proInput._id}`}
                  name='edit-pro-date'
                  value={formData.date}
                  onChange={handleInputChange('date')}
                  className='form-control'
                  style={{
                    paddingRight: '30px',
                    paddingTop: formData.date ? '20px' : '12px',
                    paddingBottom: formData.date ? '6px' : '12px',
                    paddingLeft: '12px',
                    height: 'auto',
                    minHeight: '48px',
                    background: '#ffffff',
                    border: '0.5px solid #c5d8ec',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                />
                <label
                  htmlFor={`edit-pro-date-${proInput._id}`}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: formData.date ? '6px' : '50%',
                    transform: formData.date ? 'none' : 'translateY(-50%)',
                    fontSize: formData.date ? '10px' : '14px',
                    fontWeight: formData.date ? '500' : '400',
                    textTransform: formData.date ? 'uppercase' : 'none',
                    color: formData.date ? '#185FA5' : '#999',
                    transition: 'all 0.2s ease',
                    pointerEvents: 'none'
                  }}
                >
                  Date
                </label>
                {formData.date && (
                  <button
                    type='button'
                    onClick={() => setFormData(prev => ({ ...prev, date: '' }))}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '10px',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#8aabcc',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </Row>
            <Row className='form-group mb-3'>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  id={`edit-pro-place-${proInput._id}`}
                  name='edit-pro-place'
                  value={formData.place}
                  onChange={handleInputChange('place')}
                  className='form-control'
                  style={{
                    paddingRight: '30px',
                    paddingTop: formData.place ? '20px' : '12px',
                    paddingBottom: formData.place ? '6px' : '12px',
                    paddingLeft: '12px',
                    height: 'auto',
                    minHeight: '48px',
                    background: '#ffffff',
                    border: '0.5px solid #c5d8ec',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                />
                <label
                  htmlFor={`edit-pro-place-${proInput._id}`}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: formData.place ? '6px' : '50%',
                    transform: formData.place ? 'none' : 'translateY(-50%)',
                    fontSize: formData.place ? '10px' : '14px',
                    fontWeight: formData.place ? '500' : '400',
                    textTransform: formData.place ? 'uppercase' : 'none',
                    color: formData.place ? '#185FA5' : '#999',
                    transition: 'all 0.2s ease',
                    pointerEvents: 'none'
                  }}
                >
                  Place
                </label>
                {formData.place && (
                  <button
                    type='button'
                    onClick={() => setFormData(prev => ({ ...prev, place: '' }))}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '10px',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#8aabcc',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </Row>
            <Row className='form-group mb-3'>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  id={`edit-pro-person-${proInput._id}`}
                  name='edit-pro-person'
                  value={formData.person}
                  onChange={handleInputChange('person')}
                  className='form-control'
                  style={{
                    paddingRight: '30px',
                    paddingTop: formData.person ? '20px' : '12px',
                    paddingBottom: formData.person ? '6px' : '12px',
                    paddingLeft: '12px',
                    height: 'auto',
                    minHeight: '48px',
                    background: '#ffffff',
                    border: '0.5px solid #c5d8ec',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                />
                <label
                  htmlFor={`edit-pro-person-${proInput._id}`}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: formData.person ? '6px' : '50%',
                    transform: formData.person ? 'none' : 'translateY(-50%)',
                    fontSize: formData.person ? '10px' : '14px',
                    fontWeight: formData.person ? '500' : '400',
                    textTransform: formData.person ? 'uppercase' : 'none',
                    color: formData.person ? '#185FA5' : '#999',
                    transition: 'all 0.2s ease',
                    pointerEvents: 'none'
                  }}
                >
                  Person
                </label>
                {formData.person && (
                  <button
                    type='button'
                    onClick={() => setFormData(prev => ({ ...prev, person: '' }))}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '10px',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#8aabcc',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </Row>
            <Row className='form-group mb-3'>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  id={`edit-pro-subject-${proInput._id}`}
                  name='edit-pro-subject'
                  value={formData.subject}
                  onChange={handleInputChange('subject')}
                  className='form-control'
                  style={{
                    paddingRight: '30px',
                    paddingTop: formData.subject ? '20px' : '12px',
                    paddingBottom: formData.subject ? '6px' : '12px',
                    paddingLeft: '12px',
                    height: 'auto',
                    minHeight: '48px',
                    background: '#ffffff',
                    border: '0.5px solid #c5d8ec',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                />
                <label
                  htmlFor={`edit-pro-subject-${proInput._id}`}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: formData.subject ? '6px' : '50%',
                    transform: formData.subject ? 'none' : 'translateY(-50%)',
                    fontSize: formData.subject ? '10px' : '14px',
                    fontWeight: formData.subject ? '500' : '400',
                    textTransform: formData.subject ? 'uppercase' : 'none',
                    color: formData.subject ? '#185FA5' : '#999',
                    transition: 'all 0.2s ease',
                    pointerEvents: 'none'
                  }}
                >
                  Subject
                </label>
                {formData.subject && (
                  <button
                    type='button'
                    onClick={() => setFormData(prev => ({ ...prev, subject: '' }))}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '10px',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#8aabcc',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </Row>
            <Row className='form-group mb-3'>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  id={`edit-pro-reason-${proInput._id}`}
                  name='edit-pro-reason'
                  value={formData.reason}
                  onChange={handleInputChange('reason')}
                  className='form-control'
                  style={{
                    paddingTop: formData.reason ? '20px' : '12px',
                    paddingBottom: formData.reason ? '6px' : '12px',
                    paddingLeft: '12px',
                    height: 'auto',
                    minHeight: '48px',
                    background: '#ffffff',
                    border: '0.5px solid #c5d8ec',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                />
                <label
                  htmlFor={`edit-pro-reason-${proInput._id}`}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: formData.reason ? '6px' : '50%',
                    transform: formData.reason ? 'none' : 'translateY(-50%)',
                    fontSize: formData.reason ? '10px' : '14px',
                    fontWeight: formData.reason ? '500' : '400',
                    textTransform: formData.reason ? 'uppercase' : 'none',
                    color: formData.reason ? '#185FA5' : '#999',
                    transition: 'all 0.2s ease',
                    pointerEvents: 'none'
                  }}
                >
                  Reason
                </label>
              </div>
            </Row>
            <Row className='form-group mb-3'>
              <Col sm={12}>
                <label style={{ fontSize: '10px', fontWeight: 500, textTransform: 'uppercase', color: '#185FA5', marginBottom: '8px', display: 'block' }}>
                  Condition
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                  <input
                    id={`edit-pro-condition1-${proInput._id}`}
                    name='edit-pro-condition1'
                    value={formData.condition1}
                    onChange={handleInputChange('condition1')}
                    placeholder='1'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition2-${proInput._id}`}
                    name='edit-pro-condition2'
                    value={formData.condition2}
                    onChange={handleInputChange('condition2')}
                    placeholder='2'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition3-${proInput._id}`}
                    name='edit-pro-condition3'
                    value={formData.condition3}
                    onChange={handleInputChange('condition3')}
                    placeholder='3'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition4-${proInput._id}`}
                    name='edit-pro-condition4'
                    value={formData.condition4}
                    onChange={handleInputChange('condition4')}
                    placeholder='4'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition5-${proInput._id}`}
                    name='edit-pro-condition5'
                    value={formData.condition5}
                    onChange={handleInputChange('condition5')}
                    placeholder='5'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition6-${proInput._id}`}
                    name='edit-pro-condition6'
                    value={formData.condition6}
                    onChange={handleInputChange('condition6')}
                    placeholder='6'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition7-${proInput._id}`}
                    name='edit-pro-condition7'
                    value={formData.condition7}
                    onChange={handleInputChange('condition7')}
                    placeholder='7'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition8-${proInput._id}`}
                    name='edit-pro-condition8'
                    value={formData.condition8}
                    onChange={handleInputChange('condition8')}
                    placeholder='8'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition9-${proInput._id}`}
                    name='edit-pro-condition9'
                    value={formData.condition9}
                    onChange={handleInputChange('condition9')}
                    placeholder='9'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition10-${proInput._id}`}
                    name='edit-pro-condition10'
                    value={formData.condition10}
                    onChange={handleInputChange('condition10')}
                    placeholder='10'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition11-${proInput._id}`}
                    name='edit-pro-condition11'
                    value={formData.condition11}
                    onChange={handleInputChange('condition11')}
                    placeholder='11'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition12-${proInput._id}`}
                    name='edit-pro-condition12'
                    value={formData.condition12}
                    onChange={handleInputChange('condition12')}
                    placeholder='12'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition13-${proInput._id}`}
                    name='edit-pro-condition13'
                    value={formData.condition13}
                    onChange={handleInputChange('condition13')}
                    placeholder='13'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition14-${proInput._id}`}
                    name='edit-pro-condition14'
                    value={formData.condition14}
                    onChange={handleInputChange('condition14')}
                    placeholder='14'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition15-${proInput._id}`}
                    name='edit-pro-condition15'
                    value={formData.condition15}
                    onChange={handleInputChange('condition15')}
                    placeholder='15'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition16-${proInput._id}`}
                    name='edit-pro-condition16'
                    value={formData.condition16}
                    onChange={handleInputChange('condition16')}
                    placeholder='16'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition17-${proInput._id}`}
                    name='edit-pro-condition17'
                    value={formData.condition17}
                    onChange={handleInputChange('condition17')}
                    placeholder='17'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition18-${proInput._id}`}
                    name='edit-pro-condition18'
                    value={formData.condition18}
                    onChange={handleInputChange('condition18')}
                    placeholder='18'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition19-${proInput._id}`}
                    name='edit-pro-condition19'
                    value={formData.condition19}
                    onChange={handleInputChange('condition19')}
                    placeholder='19'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                  <input
                    id={`edit-pro-condition20-${proInput._id}`}
                    name='edit-pro-condition20'
                    value={formData.condition20}
                    onChange={handleInputChange('condition20')}
                    placeholder='20'
                    className='form-control'
                    style={{ background: '#ffffff', border: '0.5px solid #c5d8ec', borderRadius: '6px', textAlign: 'center' }}
                  />
                </div>
              </Col>
            </Row>
            <Row className='form-group mb-3'>
              <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    id={`edit-pro-data-${proInput._id}`}
                    name='edit-pro-data'
                    value={formData.data}
                    onChange={handleInputChange('data')}
                    className='form-control'
                    style={{
                      paddingRight: '30px',
                      paddingTop: formData.data ? '20px' : '12px',
                      paddingBottom: formData.data ? '6px' : '12px',
                      paddingLeft: '12px',
                      height: 'auto',
                      minHeight: '48px',
                      background: '#ffffff',
                      border: '0.5px solid #c5d8ec',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease'
                    }}
                  />
                  <label
                    htmlFor={`edit-pro-data-${proInput._id}`}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: formData.data ? '6px' : '50%',
                      transform: formData.data ? 'none' : 'translateY(-50%)',
                      fontSize: formData.data ? '10px' : '14px',
                      fontWeight: formData.data ? '500' : '400',
                      textTransform: formData.data ? 'uppercase' : 'none',
                      color: formData.data ? '#185FA5' : '#999',
                      transition: 'all 0.2s ease',
                      pointerEvents: 'none'
                    }}
                  >
                    Data
                  </label>
                  {formData.data && (
                    <button
                      type='button'
                      onClick={() => setFormData(prev => ({ ...prev, data: '' }))}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        right: '10px',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#8aabcc',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    id={`edit-pro-unit-${proInput._id}`}
                    name='edit-pro-unit'
                    value={formData.unit}
                    onChange={handleInputChange('unit')}
                    className='form-control'
                    style={{
                      paddingRight: '30px',
                      paddingTop: formData.unit ? '20px' : '12px',
                      paddingBottom: formData.unit ? '6px' : '12px',
                      paddingLeft: '12px',
                      height: 'auto',
                      minHeight: '48px',
                      background: '#ffffff',
                      border: '0.5px solid #c5d8ec',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease'
                    }}
                  />
                  <label
                    htmlFor={`edit-pro-unit-${proInput._id}`}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: formData.unit ? '6px' : '50%',
                      transform: formData.unit ? 'none' : 'translateY(-50%)',
                      fontSize: formData.unit ? '10px' : '14px',
                      fontWeight: formData.unit ? '500' : '400',
                      textTransform: formData.unit ? 'uppercase' : 'none',
                      color: formData.unit ? '#185FA5' : '#999',
                      transition: 'all 0.2s ease',
                      pointerEvents: 'none'
                    }}
                  >
                    Unit
                  </label>
                  {formData.unit && (
                    <button
                      type='button'
                      onClick={() => setFormData(prev => ({ ...prev, unit: '' }))}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        right: '10px',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#8aabcc',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </Row>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
              <button
                type='button'
                onClick={toggleModal}
                style={{
                  background: 'rgba(255,255,255,0.6)',
                  border: '0.5px solid #c5d8ec',
                  color: '#4a6a8a',
                  fontWeight: 500,
                  padding: '7px 22px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type='submit'
                style={{
                  background: '#185FA5',
                  border: 'none',
                  color: '#fff',
                  fontWeight: 500,
                  padding: '7px 22px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Update
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <td
        className={'sticky-td'}
        onClick={event => handleClick(event, proInput._id)}
        style={{ textAlign: 'center', verticalAlign: 'top' }}
      >
        <button
          ref={menuButtonRef}
          onClick={handleMenuButtonClick}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            fontSize: '16px',
            color: '#666',
            lineHeight: 1
          }}
          title="More actions"
        >
          ☰
        </button>
      </td>
      <td
        name='date'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        // style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
      >
        {auth.id === proInput.user && isDateEditing ? (
          <Input
            name='date'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            autoFocus
          />
        ) : (
          highlightText(proInput.date, searchKeywords)
        )}
      </td>
      <td
        name='place'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        // style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
      >
        {auth.id === proInput.user && isPlaceEditing ? (
          <Input
            name='place'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            autoFocus
          />
        ) : proInput.place.indexOf('http') === 0 ||
          proInput.place.indexOf('/') === 0 ? (
          proInput.place ? (
            proInput.place.indexOf('http') === 0 ? (
              <a
                href={proInput.place}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.place.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {proInput.place.split('//')[1].split('/')[0].split('.')[1]}
                  </span>
                ) : proInput.place.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {proInput.place.split('//')[1].split('/')[0].split('.')[0]}
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.place.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.place.split('//')[1].split('/')[0].split('.')[1]
                    .length ? (
                  <span>
                    {proInput.place.split('//')[1].split('/')[0].split('.')[1]}
                  </span>
                ) : (
                  <span>
                    {proInput.place.split('//')[1].split('/')[0].split('.')[0]}
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.place}`}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <code>{highlightText(proInput.place, searchKeywords)}</code>
        )}
      </td>
      <td
        name='person'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
      >
        {auth.id === proInput.user && isPersonEditing ? (
          <Input
            name='person'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            autoFocus
          />
        ) : (
          highlightText(proInput.person, searchKeywords)
        )}
      </td>
      <td
        name='subject'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
      >
        {auth.id === proInput.user && isSubjectEditing ? (
          <Input
            name='subject'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            autoFocus
          />
        ) : (
          <b>{highlightText(proInput.subject, searchKeywords)}</b>
        )}
      </td>
      <td
        name='reason'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|reason'}
      style={{ position: 'relative', maxWidth: getColumnWidth('reason') }}
      >
      <div style={{ width: getColumnWidth('reason') }}>
        {auth.id === proInput.user && isReasonEditing ? (
          <textarea
            name='reason'
            value={tempValue}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            onFocus={e => {
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            autoFocus
            style={{ width: '100%', overflow: 'hidden', resize: 'vertical' }}
            rows={Math.max(5, tempValue.split('\n').length)}
          />
        ) : (
          <div
            key={`reason-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='reason'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.reason !== undefined ? pendingFields.reason : proInput.reason), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('reason')} />
      </td>
      <td
        name='condition1'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition1'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition1') }}
      >
      <div style={{ width: getColumnWidth('condition1') }}>
        {auth.id === proInput.user && isCondition1Editing ? (
          <Input
            name='condition1'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition1.indexOf('http') === 0 ||
          proInput.condition1.indexOf('/') === 0 ? (
          proInput.condition1 ? (
            proInput.condition1.indexOf('http') === 0 ? (
              <a
                href={proInput.condition1}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition1.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition1
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition1.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition1
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition1.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition1.split('//')[1].split('/')[0].split('.')[1]
                    .length ? (
                  <span>
                    {
                      proInput.condition1
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition1
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition1}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          // proInput.condition1
          <div
            key={`condition1-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition1'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition1 !== undefined ? pendingFields.condition1 : proInput.condition1), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('reason')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition1')} />
      </td>
      <td
        name='condition2'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition2'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition2') }}
      >
      <div style={{ width: getColumnWidth('condition2') }}>
        {auth.id === proInput.user && isCondition2Editing ? (
          <Input
            name='condition2'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition2.indexOf('http') === 0 ||
          proInput.condition2.indexOf('/') === 0 ? (
          proInput.condition2 ? (
            proInput.condition2.indexOf('http') === 0 ? (
              <a
                href={proInput.condition2}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition2.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition2
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition2.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition2
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition2.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition2.split('//')[1].split('/')[0].split('.')[1]
                    .length ? (
                  <span>
                    {
                      proInput.condition2
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition2
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition2}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition2-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition2'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition2 !== undefined ? pendingFields.condition2 : proInput.condition2), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition1')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition2')} />
      </td>
      <td
        name='condition3'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition3'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition3') }}
      >
      <div style={{ width: getColumnWidth('condition3') }}>
        {auth.id === proInput.user && isCondition3Editing ? (
          <Input
            name='condition3'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition3.indexOf('http') === 0 ||
          proInput.condition3.indexOf('/') === 0 ? (
          proInput.condition3 ? (
            proInput.condition3.indexOf('http') === 0 ? (
              <a
                href={proInput.condition3}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition3.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition3
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition3.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition3
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition3.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition3.split('//')[1].split('/')[0].split('.')[1]
                    .length ? (
                  <span>
                    {
                      proInput.condition3
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition3
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition3}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition3-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition3'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition3 !== undefined ? pendingFields.condition3 : proInput.condition3), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition2')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition3')} />
      </td>
      <td
        name='condition4'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition4'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition4') }}
      >
      <div style={{ width: getColumnWidth('condition4') }}>
        {auth.id === proInput.user && isCondition4Editing ? (
          <Input
            name='condition4'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition4.indexOf('http') === 0 ||
          proInput.condition4.indexOf('/') === 0 ? (
          proInput.condition4 ? (
            proInput.condition4.indexOf('http') === 0 ? (
              <a
                href={proInput.condition4}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition4.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition4
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition4.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition4
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition4.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition4.split('//')[1].split('/')[0].split('.')[1]
                    .length ? (
                  <span>
                    {
                      proInput.condition4
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition4
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition4}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition4-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition4'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition4 !== undefined ? pendingFields.condition4 : proInput.condition4), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition3')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition4')} />
      </td>

      <td
        name='condition5'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition5'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition5') }}
      >
      <div style={{ width: getColumnWidth('condition5') }}>
        {auth.id === proInput.user && isCondition5Editing ? (
          <Input
            name='condition5'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition5.indexOf('http') === 0 ||
          proInput.condition5.indexOf('/') === 0 ? (
          proInput.condition5 ? (
            proInput.condition5.indexOf('http') === 0 ? (
              <a
                href={proInput.condition5}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition5.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition5
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition5.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition5
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition5.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition5.split('//')[1].split('/')[0].split('.')[1]
                    .length ? (
                  <span>
                    {
                      proInput.condition5
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition5
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition5}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition5-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition5'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition5 !== undefined ? pendingFields.condition5 : proInput.condition5), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition4')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition5')} />
      </td>

      <td
        name='condition6'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition6'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition6') }}
      >
      <div style={{ width: getColumnWidth('condition6') }}>
        {auth.id === proInput.user && isCondition6Editing ? (
          <Input
            name='condition6'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition6.indexOf('http') === 0 ||
          proInput.condition6.indexOf('/') === 0 ? (
          proInput.condition6 ? (
            proInput.condition6.indexOf('http') === 0 ? (
              <a
                href={proInput.condition6}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition6.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition6
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition6.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition6
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition6.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition6.split('//')[1].split('/')[0].split('.')[1]
                    .length ? (
                  <span>
                    {
                      proInput.condition6
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition6
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition6}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition6-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition6'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition6 !== undefined ? pendingFields.condition6 : proInput.condition6), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition5')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition6')} />
      </td>

      <td
        name='condition7'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition7'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition7') }}
      >
      <div style={{ width: getColumnWidth('condition7') }}>
        {auth.id === proInput.user && isCondition7Editing ? (
          <Input
            name='condition7'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition7.indexOf('http') === 0 ||
          proInput.condition7.indexOf('/') === 0 ? (
          proInput.condition7 ? (
            proInput.condition7.indexOf('http') === 0 ? (
              <a
                href={proInput.condition7}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition7.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition7
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition7.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition7
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition7.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition7.split('//')[1].split('/')[0].split('.')[1]
                    .length ? (
                  <span>
                    {
                      proInput.condition7
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition7
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition7}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition7-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition7'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition7 !== undefined ? pendingFields.condition7 : proInput.condition7), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition6')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition7')} />
      </td>

      <td
        name='condition8'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition8'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition8') }}
      >
      <div style={{ width: getColumnWidth('condition8') }}>
        {auth.id === proInput.user && isCondition8Editing ? (
          <Input
            name='condition8'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition8.indexOf('http') === 0 ||
          proInput.condition8.indexOf('/') === 0 ? (
          proInput.condition8 ? (
            proInput.condition8.indexOf('http') === 0 ? (
              <a
                href={proInput.condition8}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition8.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition8
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition8.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition8
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition8.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition8.split('//')[1].split('/')[0].split('.')[1]
                    .length ? (
                  <span>
                    {
                      proInput.condition8
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition8
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition8}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition8-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition8'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition8 !== undefined ? pendingFields.condition8 : proInput.condition8), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition7')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition8')} />
      </td>

      <td
        name='condition9'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition9'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition9') }}
      >
      <div style={{ width: getColumnWidth('condition9') }}>
        {auth.id === proInput.user && isCondition9Editing ? (
          <Input
            name='condition9'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition9.indexOf('http') === 0 ||
          proInput.condition9.indexOf('/') === 0 ? (
          proInput.condition9 ? (
            proInput.condition9.indexOf('http') === 0 ? (
              <a
                href={proInput.condition9}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition9.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition9
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition9.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition9
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition9.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition9.split('//')[1].split('/')[0].split('.')[1]
                    .length ? (
                  <span>
                    {
                      proInput.condition9
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition9
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition9}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition9-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition9'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition9 !== undefined ? pendingFields.condition9 : proInput.condition9), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition8')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition9')} />
      </td>

      <td
        name='condition10'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition10'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition10') }}
      >
      <div style={{ width: getColumnWidth('condition10') }}>
        {auth.id === proInput.user && isCondition10Editing ? (
          <Input
            name='condition10'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition10.indexOf('http') === 0 ||
          proInput.condition10.indexOf('/') === 0 ? (
          proInput.condition10 ? (
            proInput.condition10.indexOf('http') === 0 ? (
              <a
                href={proInput.condition10}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition10.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition10
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition10.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition10
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition10.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition10
                    .split('//')[1]
                    .split('/')[0]
                    .split('.')[1].length ? (
                  <span>
                    {
                      proInput.condition10
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition10
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition10}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition10-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition10'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition10 !== undefined ? pendingFields.condition10 : proInput.condition10), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition9')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition10')} />
      </td>

      <td
        name='condition11'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition11'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition11') }}
      >
      <div style={{ width: getColumnWidth('condition11') }}>
        {auth.id === proInput.user && isCondition11Editing ? (
          <Input
            name='condition11'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition11.indexOf('http') === 0 ||
          proInput.condition11.indexOf('/') === 0 ? (
          proInput.condition11 ? (
            proInput.condition11.indexOf('http') === 0 ? (
              <a
                href={proInput.condition11}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition11.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition11
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition11.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition11
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition11.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition11
                    .split('//')[1]
                    .split('/')[0]
                    .split('.')[1].length ? (
                  <span>
                    {
                      proInput.condition11
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition11
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition11}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition11-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition11'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition11 !== undefined ? pendingFields.condition11 : proInput.condition11), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition10')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition11')} />
      </td>
      <td
        name='condition12'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition12'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition12') }}
      >
      <div style={{ width: getColumnWidth('condition12') }}>
        {auth.id === proInput.user && isCondition12Editing ? (
          <Input
            name='condition12'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition12.indexOf('http') === 0 ||
          proInput.condition12.indexOf('/') === 0 ? (
          proInput.condition12 ? (
            proInput.condition12.indexOf('http') === 0 ? (
              <a
                href={proInput.condition12}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition12.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition12
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition12.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition12
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition12.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition12
                    .split('//')[1]
                    .split('/')[0]
                    .split('.')[1].length ? (
                  <span>
                    {
                      proInput.condition12
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition12
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition12}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition12-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition12'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition12 !== undefined ? pendingFields.condition12 : proInput.condition12), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition11')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition12')} />
      </td>

      <td
        name='condition13'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition13'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition13') }}
      >
      <div style={{ width: getColumnWidth('condition13') }}>
        {auth.id === proInput.user && isCondition13Editing ? (
          <Input
            name='condition13'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition13.indexOf('http') === 0 ||
          proInput.condition13.indexOf('/') === 0 ? (
          proInput.condition13 ? (
            proInput.condition13.indexOf('http') === 0 ? (
              <a
                href={proInput.condition13}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition13.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition13
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition13.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition13
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition13.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition13
                    .split('//')[1]
                    .split('/')[0]
                    .split('.')[1].length ? (
                  <span>
                    {
                      proInput.condition13
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition13
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition13}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition13-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition13'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition13 !== undefined ? pendingFields.condition13 : proInput.condition13), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition12')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition13')} />
      </td>

      <td
        name='condition14'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition14'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition14') }}
      >
      <div style={{ width: getColumnWidth('condition14') }}>
        {auth.id === proInput.user && isCondition14Editing ? (
          <Input
            name='condition14'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition14.indexOf('http') === 0 ||
          proInput.condition14.indexOf('/') === 0 ? (
          proInput.condition14 ? (
            proInput.condition14.indexOf('http') === 0 ? (
              <a
                href={proInput.condition14}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition14.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition14
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition14.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition14
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition14.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition14
                    .split('//')[1]
                    .split('/')[0]
                    .split('.')[1].length ? (
                  <span>
                    {
                      proInput.condition14
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition14
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition14}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition14-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition14'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition14 !== undefined ? pendingFields.condition14 : proInput.condition14), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition13')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition14')} />
      </td>

      <td
        name='condition15'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition15'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition15') }}
      >
      <div style={{ width: getColumnWidth('condition15') }}>
        {auth.id === proInput.user && isCondition15Editing ? (
          <Input
            name='condition15'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition15.indexOf('http') === 0 ||
          proInput.condition15.indexOf('/') === 0 ? (
          proInput.condition15 ? (
            proInput.condition15.indexOf('http') === 0 ? (
              <a
                href={proInput.condition15}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition15.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition15
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition15.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition15
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition15.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition15
                    .split('//')[1]
                    .split('/')[0]
                    .split('.')[1].length ? (
                  <span>
                    {
                      proInput.condition15
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition15
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition15}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition15-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition15'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition15 !== undefined ? pendingFields.condition15 : proInput.condition15), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition14')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition15')} />
      </td>
      <td
        name='condition16'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition16'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition16') }}
      >
      <div style={{ width: getColumnWidth('condition16') }}>
        {auth.id === proInput.user && isCondition16Editing ? (
          <Input
            name='condition16'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition16.indexOf('http') === 0 ||
          proInput.condition16.indexOf('/') === 0 ? (
          proInput.condition16 ? (
            proInput.condition16.indexOf('http') === 0 ? (
              <a
                href={proInput.condition16}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition16.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition16
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition16.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition16
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition16.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition16
                    .split('//')[1]
                    .split('/')[0]
                    .split('.')[1].length ? (
                  <span>
                    {
                      proInput.condition16
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition16
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition16}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition16-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition16'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition16 !== undefined ? pendingFields.condition16 : proInput.condition16), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition15')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition16')} />
      </td>
      <td
        name='condition17'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition17'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition17') }}
      >
      <div style={{ width: getColumnWidth('condition17') }}>
        {auth.id === proInput.user && isCondition17Editing ? (
          <Input
            name='condition17'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition17.indexOf('http') === 0 ||
          proInput.condition17.indexOf('/') === 0 ? (
          proInput.condition17 ? (
            proInput.condition17.indexOf('http') === 0 ? (
              <a
                href={proInput.condition17}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition17.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition17
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition17.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition17
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition17.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition17
                    .split('//')[1]
                    .split('/')[0]
                    .split('.')[1].length ? (
                  <span>
                    {
                      proInput.condition17
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition17
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition17}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition17-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition17'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition17 !== undefined ? pendingFields.condition17 : proInput.condition17), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition16')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition17')} />
      </td>
      <td
        name='condition18'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition18'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition18') }}
      >
      <div style={{ width: getColumnWidth('condition18') }}>
        {auth.id === proInput.user && isCondition18Editing ? (
          <Input
            name='condition18'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition18.indexOf('http') === 0 ||
          proInput.condition18.indexOf('/') === 0 ? (
          proInput.condition18 ? (
            proInput.condition18.indexOf('http') === 0 ? (
              <a
                href={proInput.condition18}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition18.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition18
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition18.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition18
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition18.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition18
                    .split('//')[1]
                    .split('/')[0]
                    .split('.')[1].length ? (
                  <span>
                    {
                      proInput.condition18
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition18
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition18}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition18-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition18'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition18 !== undefined ? pendingFields.condition18 : proInput.condition18), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition17')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition18')} />
      </td>

      <td
        name='condition19'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition19'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition19') }}
      >
      <div style={{ width: getColumnWidth('condition19') }}>
        {auth.id === proInput.user && isCondition19Editing ? (
          <Input
            name='condition19'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition19.indexOf('http') === 0 ||
          proInput.condition19.indexOf('/') === 0 ? (
          proInput.condition19 ? (
            proInput.condition19.indexOf('http') === 0 ? (
              <a
                href={proInput.condition19}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition19.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition19
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition19.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition19
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition19.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition19
                    .split('//')[1]
                    .split('/')[0]
                    .split('.')[1].length ? (
                  <span>
                    {
                      proInput.condition19
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition19
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition19}`}
                target='_blank'
                rel='noreferrer'
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition19-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition19'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition19 !== undefined ? pendingFields.condition19 : proInput.condition19), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition18')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition19')} />
      </td>
      <td
        name='condition20'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
        data-cellid={proInput._id + '|condition20'}
      style={{ position: 'relative', maxWidth: getColumnWidth('condition20') }}
      >
      <div style={{ width: getColumnWidth('condition20') }}>
        {auth.id === proInput.user && isCondition20Editing ? (
          <Input
            name='condition20'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            onPaste={e => handleTextareaImagePaste(e, (tag, start, end) => {
              setTempValue(prev => prev.slice(0, start) + tag + prev.slice(end));
            })}
            onDoubleClick={selectImageTagOnDoubleClick}
            autoFocus
          />
        ) : proInput.condition20.indexOf('http') === 0 ||
          proInput.condition20.indexOf('/') === 0 ? (
          proInput.condition20 ? (
            proInput.condition20.indexOf('http') === 0 ? (
              <a
                href={proInput.condition20}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.condition20.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {
                      proInput.condition20
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : proInput.condition20.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {
                      proInput.condition20
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.condition20.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.condition20
                    .split('//')[1]
                    .split('/')[0]
                    .split('.')[1].length ? (
                  <span>
                    {
                      proInput.condition20
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[1]
                    }
                  </span>
                ) : (
                  <span>
                    {
                      proInput.condition20
                        .split('//')[1]
                        .split('/')[0]
                        .split('.')[0]
                    }
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.condition20}`}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <div
            key={`condition20-${proInput._id}-${contentVersion}-${htmlResumeRevision}`}
            name='condition20'
            onDoubleClick={handleDoubleClick} // Ensure double-click works
            onClick={handleImageClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(highlightHTML(renderMarkdown(pendingFields.condition20 !== undefined ? pendingFields.condition20 : proInput.condition20), searchKeywords))
            }}
          />
        )}
      </div>
      <ColumnResizeHandle onMouseDown={startResize('condition19')} side='left' />

      <ColumnResizeHandle onMouseDown={startResize('condition20')} />
      </td>

      <td
        name='data'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        // style={
        //   isNaN(proInput.data.replace(/,/g, '')) ? {} : { textAlign: 'right' }
        // }
        onClick={event => handleClick(event, proInput._id)}
        // style={dataStyle}
      >
        {auth.id === proInput.user && isDataEditing ? (
          <Input
            name='data'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            autoFocus
          />
        ) : proInput.data.indexOf('http') === 0 ||
          proInput.data.indexOf('/') === 0 ? (
          proInput.data ? (
            proInput.data.indexOf('http') === 0 ? (
              <a
                href={proInput.data}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                {0 ? (
                  // {0 ? (
                  <span className='fa fa-link'></span>
                ) : proInput.data.indexOf('www') !== -1 ? (
                  // If the first word is 'www', use the second word
                  <span>
                    {proInput.data.split('//')[1].split('/')[0].split('.')[1]}
                  </span>
                ) : proInput.data.split('//')[1].split('/')[0].split('.')
                    .length === 2 || 1 ? (
                  // otherwise and if domain consists of 2 words, use the first word
                  <span>
                    {proInput.data.split('//')[1].split('/')[0].split('.')[0]}
                  </span>
                ) : // otherwise use whichever is longer among the first and second words
                proInput.data.split('//')[1].split('/')[0].split('.')[0]
                    .length <
                  proInput.data.split('//')[1].split('/')[0].split('.')[1]
                    .length ? (
                  <span>
                    {proInput.data.split('//')[1].split('/')[0].split('.')[1]}
                  </span>
                ) : (
                  <span>
                    {proInput.data.split('//')[1].split('/')[0].split('.')[0]}
                  </span>
                )}
              </a>
            ) : (
              <a
                href={`file:${proInput.data}`}
                target='_blank'
                rel='noreferrer'
                onClick={handleLinkClick}
              >
                <span className='fa fa-folder-open'></span>
              </a>
            )
          ) : (
            ''
          )
        ) : (
          <code>{highlightText(proInput.data, searchKeywords)}</code>
        )}
      </td>
      <td
        name='unit'
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        className={`${isFullContentShown ? '' : 'ellipsis'}`}
        onKeyDown={handleKeyDown}
        onClick={event => handleClick(event, proInput._id)}
        //style={{ backgroundColor: itemColors[proInput._id] || 'white' }}
      >
        {auth.id === proInput.user && isUnitEditing ? (
          <Input
            name='unit'
            type='text'
            value={tempValue}
            // onBlur={handleBlur}
            onChange={handleChange}
            autoFocus
          />
        ) : (
          highlightText(proInput.unit, searchKeywords)
        )}
      </td>
    </tr>
  );
}

export let proInput_num;

export default function ListData (props) {
  const containerRef = useRef(null); // Reference to the component's container
  const [pinEnabled, setPinEnabled] = usePinEnabled();
  const [manualResizeEnabled, setManualResizeEnabled] = useManualResizeEnabled();
  const { getColumnWidth, startResize } = useColumnWidths('columnWidths.pro', { enabled: manualResizeEnabled });
  const [searchParams] = useSearchParams();
  const dispatchForDeepLink = useDispatch();
  const deepLinkHandledIdRef = useRef(null);
  // Computed during render (see pageById below); applied in an effect rather
  // than during render itself, so it isn't affected by StrictMode
  // double-invoking the render body.
  const pendingDeepLinkPageRef = useRef(null);

  useEffect(() => {
    const pending = pendingDeepLinkPageRef.current;
    if (pending && deepLinkHandledIdRef.current !== pending.id) {
      deepLinkHandledIdRef.current = pending.id;
      setCurrentPage(pending.page);
    }
  });

  // Deep linking: scroll to and highlight the item when URL has ?id=
  // parameter. Which page to jump to is figured out live during render (see
  // pageById below) rather than from a stored page number, since a fixed
  // page number would go stale as new records shift everything's position.
  useEffect(() => {
    const itemId = searchParams.get('id');
    if (itemId && !props.isLoading) {
      // Poll for the row instead of a fixed delay - re-sorting/paginating a
      // large list can take longer than any one fixed timeout to reach the DOM.
      let attempts = 0;
      const findAndScrollToRow = () => {
        const row = document.querySelector(`tr[data-row-id="${itemId}"]`);
        if (row) {
          // Scroll to the row
          row.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight the row temporarily
          dispatchForDeepLink({
            type: 'SET_ITEM_COLOR',
            payload: { id: itemId, color: '#fffacd' } // Light yellow highlight
          });
          // Flash effect
          setTimeout(() => {
            row.style.transition = 'background-color 0.5s';
            dispatchForDeepLink({
              type: 'SET_ITEM_COLOR',
              payload: { id: itemId, color: '#d6eaf8' } // Light blue selection
            });
          }, 500);
        } else if (attempts < 20) {
          attempts += 1;
          setTimeout(findAndScrollToRow, 100);
        }
      };
      setTimeout(findAndScrollToRow, 100);
    }
  }, [searchParams, props.isLoading, dispatchForDeepLink]);

  // Reset to page 1 whenever the search keyword actually changes, so a
  // search that only matches items on an earlier page doesn't leave the view
  // stuck on a now-empty page. Compares against the previous value (rather
  // than a "have I run once" flag) so it stays a no-op on mount even under
  // StrictMode's double-invoked effects, and doesn't clobber a deep link's
  // page jump.
  const previousKeywordRef = useRef(props.keyword);
  useEffect(() => {
    if (previousKeywordRef.current !== props.keyword) {
      setCurrentPage(1);
    }
    previousKeywordRef.current = props.keyword;
  }, [props.keyword]);

  useEffect(() => {
    // Prevent Tab key from selecting actionable elements within this component
    const handleKeyDown = event => {
      if (event.key === 'Tab') {
        event.preventDefault(); // Prevent the default Tab behavior
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
    }

    // Cleanup the event listener on component unmount
    return () => {
      if (container) {
        container.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, []);

  // A previous "fix" for iOS Safari resetting input values after long tab
  // suspension used to live here: it saved every embedded input/textarea's
  // value to localStorage keyed only by its DOM position (row id + cell name
  // + index within the cell), then blindly wrote those saved values back in
  // on visibilitychange/pageshow (i.e. whenever the phone wakes up or the
  // tab regains focus) - with no check on how old the saved value was or
  // whether it still matched what actually belonged in that position. Once
  // an entry for a given position existed, it would keep getting restored
  // over whatever was actually, correctly rendered there, including a stale
  // copy from days/weeks earlier - which is exactly the "old content comes
  // back after waking the phone" bug. Removed rather than patched: the
  // official edit flow already avoids this entirely (it's a React-controlled
  // textarea/Input, unaffected by iOS resetting the raw DOM value), and this
  // one-time cleanup just clears out any stale snapshot it left behind.
  useEffect(() => {
    localStorage.removeItem('kirkProInputValues');
  }, []);

  const scrollContainerRef = useRef(null); // Define the ref here

  const isFullContentShown = useSelector(
    state => state.fieldButtons.isFullContentShown
  );

  const toggledProInputFieldButton = useSelector(
    state => state.fieldButtons.toggledProInputFieldButton
  );

  const isProInputIdAscending = useSelector(
    state => state.fieldButtons.isProInputIdAscending
  );
  const isProInputDateAscending = useSelector(
    state => state.fieldButtons.isProInputDateAscending
  );
  const isProInputPlaceAscending = useSelector(
    state => state.fieldButtons.isProInputPlaceAscending
  );
  const isProInputPersonAscending = useSelector(
    state => state.fieldButtons.isProInputPersonAscending
  );
  const isProInputSubjectAscending = useSelector(
    state => state.fieldButtons.isProInputSubjectAscending
  );
  const isProInputReasonAscending = useSelector(
    state => state.fieldButtons.isProInputReasonAscending
  );
  const isProInputCondition1Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition1Ascending
  );
  const isProInputCondition2Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition2Ascending
  );
  const isProInputCondition3Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition3Ascending
  );
  const isProInputCondition4Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition4Ascending
  );
  const isProInputCondition5Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition5Ascending
  );
  const isProInputCondition6Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition6Ascending
  );
  const isProInputCondition7Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition7Ascending
  );
  const isProInputCondition8Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition8Ascending
  );
  const isProInputCondition9Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition9Ascending
  );
  const isProInputCondition10Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition10Ascending
  );
  const isProInputCondition11Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition11Ascending
  );
  const isProInputCondition12Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition12Ascending
  );
  const isProInputCondition13Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition13Ascending
  );
  const isProInputCondition14Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition14Ascending
  );
  const isProInputCondition15Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition15Ascending
  );
  const isProInputCondition16Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition16Ascending
  );
  const isProInputCondition17Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition17Ascending
  );
  const isProInputCondition18Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition18Ascending
  );
  const isProInputCondition19Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition19Ascending
  );
  const isProInputCondition20Ascending = useSelector(
    state => state.fieldButtons.isProInputCondition20Ascending
  );
  const isProInputDataAscending = useSelector(
    state => state.fieldButtons.isProInputDataAscending
  );
  const isProInputUnitAscending = useSelector(
    state => state.fieldButtons.isProInputUnitAscending
  );

  const dispatch = useDispatch();
  const toggleIsProInputIdAscending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_ID_ASCENDING' });
  };
  const toggleIsProInputDateAscending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_DATE_ASCENDING' });
  };
  const toggleIsProInputPlaceAscending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_PLACE_ASCENDING' });
  };
  const toggleIsProInputPersonAscending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_PERSON_ASCENDING' });
  };
  const toggleIsProInputSubjectAscending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_SUBJECT_ASCENDING' });
  };
  const toggleIsProInputReasonAscending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_REASON_ASCENDING' });
  };
  const toggleIsProInputCondition1Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION1_ASCENDING' });
  };
  const toggleIsProInputCondition2Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION2_ASCENDING' });
  };
  const toggleIsProInputCondition3Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION3_ASCENDING' });
  };
  const toggleIsProInputCondition4Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION4_ASCENDING' });
  };
  const toggleIsProInputCondition5Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION5_ASCENDING' });
  };
  const toggleIsProInputCondition6Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION6_ASCENDING' });
  };
  const toggleIsProInputCondition7Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION7_ASCENDING' });
  };
  const toggleIsProInputCondition8Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION8_ASCENDING' });
  };
  const toggleIsProInputCondition9Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION9_ASCENDING' });
  };
  const toggleIsProInputCondition10Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION10_ASCENDING' });
  };
  const toggleIsProInputCondition11Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION11_ASCENDING' });
  };
  const toggleIsProInputCondition12Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION12_ASCENDING' });
  };
  const toggleIsProInputCondition13Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION13_ASCENDING' });
  };
  const toggleIsProInputCondition14Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION14_ASCENDING' });
  };
  const toggleIsProInputCondition15Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION15_ASCENDING' });
  };
  const toggleIsProInputCondition16Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION16_ASCENDING' });
  };
  const toggleIsProInputCondition17Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION17_ASCENDING' });
  };
  const toggleIsProInputCondition18Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION18_ASCENDING' });
  };
  const toggleIsProInputCondition19Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION19_ASCENDING' });
  };
  const toggleIsProInputCondition20Ascending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_CONDITION20_ASCENDING' });
  };
  const toggleIsProInputDataAscending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_DATA_ASCENDING' });
  };
  const toggleIsProInputUnitAscending = () => {
    dispatch({ type: 'TOGGLE_IS_PRO_INPUT_UNIT_ASCENDING' });
  };
  const toggleIsFullContentShown = () => {
    dispatch({ type: 'TOGGLE_IS_FULL_CONTENT_SHOWN' });
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [proInputListPerPage] = useState(100);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [savedScrollPosition, setSavedScrollPosition] = useState(null);
  const [savedPage, setSavedPage] = useState(null);
  const [isAtTop, setIsAtTop] = useState(true);

  const paginate = pageNumber => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Show/hide scroll-to-top button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      // Show button when scrolled down more than 1000px
      setShowScrollButton(window.scrollY > 1000);
      // Consider "at top" when under 1000px threshold
      setIsAtTop(window.scrollY < 1000);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle between top and saved position
  const toggleScrollPosition = () => {
    if (isAtTop && savedScrollPosition !== null) {
      // DOWN arrow clicked - go back to the saved page and scroll position
      if (savedPage !== null && savedPage !== currentPage) {
        setCurrentPage(savedPage);
        // Wait for the restored page's content to render before scrolling
        // to a non-zero offset, or the browser clamps to the old content height
        setTimeout(() => {
          window.scrollTo({ top: savedScrollPosition, behavior: 'smooth' });
        }, 100);
      } else {
        window.scrollTo({
          top: savedScrollPosition,
          behavior: 'smooth'
        });
      }
      setSavedScrollPosition(null);
      setSavedPage(null);
    } else {
      // UP arrow clicked - go to the very top of the list (page 1), not just
      // the top of whichever page happens to be showing
      if (savedScrollPosition === null) {
        // Only save position if none exists (keep previous position)
        setSavedScrollPosition(window.scrollY);
        setSavedPage(currentPage);
      }
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Floating button styles - simple arrow, no circle
  // Show button when scrolled down OR when at top with a saved position to go back to
  const shouldShowButton = showScrollButton || (isAtTop && savedScrollPosition !== null);
  const scrollButtonStyle = {
    position: 'fixed',
    top: '70px',
    right: '20px',
    zIndex: 1000,
    backgroundColor: 'transparent',
    color: '#999',
    border: 'none',
    cursor: 'pointer',
    display: shouldShowButton ? 'block' : 'none',
    fontSize: '28px',
    padding: '10px',
    transition: 'opacity 0.3s'
  };

  if (props.isLoading) {
    return (
      <div className='container'>
        <div className='row'>
          <Loading version={props.version} />
        </div>
      </div>
    );
  } else if (props.errMess) {
    return (
      <div className='container'>
        <div className='row'>
          <h4>{props.errMess}</h4>
        </div>
      </div>
    );
  } else if (props.keyword !== '') {
    // console.log('setIsKeywordChanged is now ', setIsKeywordChanged);

    const keywordAndArray = props.keyword.split('|');
    var keywords = [];
    var exwords = [];
    for (let k = 0; k < keywordAndArray.length; k++) {
      let keywordArray = keywordAndArray[k].split(/[\s\u3000]+/);
      var keywords_temp = [];
      var exwords_temp = [];
      let j = 0;
      let m = 0;
      for (let i = 0; i < keywordArray.length; i++) {
        if ((keywordArray[i] !== ' ') & (keywordArray[i] !== '')) {
          if (keywordArray[i].split('')[0] === '!') {
            if (keywordArray[i].length === 2) {
              exwords_temp[m] = keywordArray[i]
                // .toLowerCase()
                .split('')
                .splice(1)
                .join('');
            } else {
              exwords_temp[m] = keywordArray[i]
                .toLowerCase()
                .split('')
                .splice(1)
                .join('');
            }
            m = m + 1;
          } else {
            if (keywordArray[i].length === 1) {
              keywords_temp[j] = keywordArray[i];
            } else {
              keywords_temp[j] = keywordArray[i].toLowerCase();
            }
            // keywords_temp[j] = keywordArray[i].toLowerCase();
            j = j + 1;
          }
        }
      }
      keywords.push(keywords_temp); // OR演算子で分割した各々のキーワード群
      exwords.push(exwords_temp);
    }
    var foundProInputs = [];
    for (let i = 0; i < keywords.length; i++) {
      //     for (let j = 0; j < keywords[i].length; j++) {
      const foundProInputs_temp = props.proInputs.filter(curr => {
        const test = keywords[i].map(keyword => {
          if (keyword.length === 1) {
            return (
              curr.date.includes(keyword) ||
              curr.place.includes(keyword) ||
              curr.person.includes(keyword) ||
              curr.subject.includes(keyword) ||
              curr.reason.includes(keyword) ||
              curr.condition1.includes(keyword) ||
              curr.condition2.includes(keyword) ||
              curr.condition3.includes(keyword) ||
              curr.condition4.includes(keyword) ||
              curr.condition5.includes(keyword) ||
              curr.condition6.includes(keyword) ||
              curr.condition7.includes(keyword) ||
              curr.condition8.includes(keyword) ||
              curr.condition9.includes(keyword) ||
              curr.condition10.includes(keyword) ||
              curr.condition11.includes(keyword) ||
              curr.condition12.includes(keyword) ||
              curr.condition13.includes(keyword) ||
              curr.condition14.includes(keyword) ||
              curr.condition15.includes(keyword) ||
              curr.condition16.includes(keyword) ||
              curr.condition17.includes(keyword) ||
              curr.condition18.includes(keyword) ||
              curr.condition19.includes(keyword) ||
              curr.condition20.includes(keyword) ||
              curr.data.includes(keyword) ||
              curr.unit.includes(keyword)
            );
          } else {
            return (
              curr.date.toLowerCase().includes(keyword) ||
              curr.place.toLowerCase().includes(keyword) ||
              curr.person.toLowerCase().includes(keyword) ||
              curr.subject.toLowerCase().includes(keyword) ||
              curr.reason.toLowerCase().includes(keyword) ||
              curr.condition1.toLowerCase().includes(keyword) ||
              curr.condition2.toLowerCase().includes(keyword) ||
              curr.condition3.toLowerCase().includes(keyword) ||
              curr.condition4.toLowerCase().includes(keyword) ||
              curr.condition5.toLowerCase().includes(keyword) ||
              curr.condition6.toLowerCase().includes(keyword) ||
              curr.condition7.toLowerCase().includes(keyword) ||
              curr.condition8.toLowerCase().includes(keyword) ||
              curr.condition9.toLowerCase().includes(keyword) ||
              curr.condition10.toLowerCase().includes(keyword) ||
              curr.condition11.toLowerCase().includes(keyword) ||
              curr.condition12.toLowerCase().includes(keyword) ||
              curr.condition13.toLowerCase().includes(keyword) ||
              curr.condition14.toLowerCase().includes(keyword) ||
              curr.condition15.toLowerCase().includes(keyword) ||
              curr.condition16.toLowerCase().includes(keyword) ||
              curr.condition17.toLowerCase().includes(keyword) ||
              curr.condition18.toLowerCase().includes(keyword) ||
              curr.condition19.toLowerCase().includes(keyword) ||
              curr.condition20.toLowerCase().includes(keyword) ||
              curr.data.toLowerCase().includes(keyword) ||
              curr.unit.toLowerCase().includes(keyword)
            );
          }
        });
        return test.every(logic => logic === true);
      });
      foundProInputs = foundProInputs.concat(foundProInputs_temp);
      //     }
    }
    var foundProInputs2 = [];
    for (let i = 0; i < exwords.length; i++) {
      //     for (let j = 0; j < keywords[i].length; j++) {
      const foundProInputs2_temp = foundProInputs.filter(curr => {
        const test = exwords[i].map(exword => {
          if (exword.length === 1) {
            return (
              curr.date.includes(exword) ||
              curr.place.includes(exword) ||
              curr.person.includes(exword) ||
              curr.subject.includes(exword) ||
              curr.reason.includes(exword) ||
              curr.condition1.includes(exword) ||
              curr.condition2.includes(exword) ||
              curr.condition3.includes(exword) ||
              curr.condition4.includes(exword) ||
              curr.condition5.includes(exword) ||
              curr.condition6.includes(exword) ||
              curr.condition7.includes(exword) ||
              curr.condition8.includes(exword) ||
              curr.condition9.includes(exword) ||
              curr.condition10.includes(exword) ||
              curr.condition11.includes(exword) ||
              curr.condition12.includes(exword) ||
              curr.condition13.includes(exword) ||
              curr.condition14.includes(exword) ||
              curr.condition15.includes(exword) ||
              curr.condition16.includes(exword) ||
              curr.condition17.includes(exword) ||
              curr.condition18.includes(exword) ||
              curr.condition19.includes(exword) ||
              curr.condition20.includes(exword) ||
              curr.data.includes(exword) ||
              curr.unit.includes(exword)
            );
          } else {
            return (
              curr.date.toLowerCase().includes(exword) ||
              curr.place.toLowerCase().includes(exword) ||
              curr.person.toLowerCase().includes(exword) ||
              curr.subject.toLowerCase().includes(exword) ||
              curr.reason.toLowerCase().includes(exword) ||
              curr.condition1.toLowerCase().includes(exword) ||
              curr.condition2.toLowerCase().includes(exword) ||
              curr.condition3.toLowerCase().includes(exword) ||
              curr.condition4.toLowerCase().includes(exword) ||
              curr.condition5.toLowerCase().includes(exword) ||
              curr.condition6.toLowerCase().includes(exword) ||
              curr.condition7.toLowerCase().includes(exword) ||
              curr.condition8.toLowerCase().includes(exword) ||
              curr.condition9.toLowerCase().includes(exword) ||
              curr.condition10.toLowerCase().includes(exword) ||
              curr.condition11.toLowerCase().includes(exword) ||
              curr.condition12.toLowerCase().includes(exword) ||
              curr.condition13.toLowerCase().includes(exword) ||
              curr.condition14.toLowerCase().includes(exword) ||
              curr.condition15.toLowerCase().includes(exword) ||
              curr.condition16.toLowerCase().includes(exword) ||
              curr.condition17.toLowerCase().includes(exword) ||
              curr.condition18.toLowerCase().includes(exword) ||
              curr.condition19.toLowerCase().includes(exword) ||
              curr.condition20.toLowerCase().includes(exword) ||
              curr.data.toLowerCase().includes(exword) ||
              curr.unit.toLowerCase().includes(exword)
            );
          }
        });
        return test.every(logic => logic === false);
      });
      foundProInputs2 = foundProInputs2.concat(foundProInputs2_temp);
      //     }
      // sortedProInputs.sort((a, b) => {
      //   return a._id.localeCompare(b._id);
      // });

      foundProInputs2.sort((a, b) => {
        return a._id.localeCompare(b._id);
      });
    }

    const conv = proInputDate => {
      let day = proInputDate.split(' ').find(element => element.includes('-'));
      if (day) {
        const len = day.split('-')[0].length;

        if (!day.split('-')[0]) {
          // console.error(`Unable to parse date from "${inputDate}"`);
          return '';
        }

        if (!day.split('-')[1]) {
          // console.error(`Unable to parse date from "${inputDate}"`);
          return '';
        }

        if (!day.split('-')[2]) {
          // console.error(`Unable to parse date from "${inputDate}"`);
          return '';
        }

        const firstnumber = day.split('-')[0].padStart(2, '0');
        const secondnumber = day.split('-')[1].padStart(2, '0');
        const thirdnumber = day.split('-')[2].padStart(2, '0');

        let time = proInputDate
          .split(' ')
          .find(element => element.includes(':'));
        if (!time) {
          if (len === 1 || len === 2) {
            day = `${secondnumber}-${thirdnumber}-${firstnumber} 00:00:00`;
            // console.log('day + time is', `${day} ${time}`);
          } else {
            day = `${firstnumber}-${secondnumber}-${thirdnumber} 00:00:00`;
            // console.log('day + time is', `${day} ${time}`);
          }
        } else {
          const [hour, minute] = time
            .split(':')
            .map(num => num.padStart(2, '0'));
          if (len === 1 || len === 2) {
            day = `${secondnumber}-${thirdnumber}-${firstnumber} ${hour}:${minute}:00`;
            // console.log('day + time is', `${day} ${time}`);
          } else {
            day = `${firstnumber}-${secondnumber}-${thirdnumber} ${hour}:${minute}:00`;
            // console.log('day + time is', `${day} ${time}`);
          }
        }
      }

      // const time = proInputDate
      //   .split(' ')
      //   .find(element => element.includes(':'));

      if (!day) {
        // console.error(`Unable to parse date from "${inputDate}"`);
        return '';
      }

      return day;
    };

    switch (toggledProInputFieldButton) {
      case 'ID':
        if (isProInputIdAscending === false) {
          foundProInputs2.sort((a, b) => {
            return a._id.localeCompare(b._id);
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b._id.localeCompare(a._id);
          });
        }
        break;

      case 'DATE':
        if (isProInputDateAscending === false) {
          foundProInputs2.sort((a, b) => {
            // return a.date.localeCompare(b.date);
            return conv(a.date).localeCompare(conv(b.date));
            // return new Date(conv(a.date)) - new Date(conv(b.date));
          });
        } else {
          foundProInputs2.sort((a, b) => {
            // return b.date.localeCompare(a.date);
            return conv(b.date).localeCompare(conv(a.date));
            // return new Date(conv(b.date)) - new Date(conv(a.date));
          });
        }

        break;
      case 'PLACE':
        if (isProInputPlaceAscending === false) {
          foundProInputs2.sort((a, b) => {
            return a.place.localeCompare(b.place);
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.place.localeCompare(a.place);
          });
        }
        break;
      case 'PERSON':
        if (isProInputPersonAscending === false) {
          foundProInputs2.sort((a, b) => {
            return a.person.localeCompare(b.person);
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.person.localeCompare(a.person);
          });
        }
        break;
      case 'SUBJECT':
        if (isProInputSubjectAscending === false) {
          foundProInputs2.sort((a, b) => {
            return a.subject.localeCompare(b.subject);
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.subject.localeCompare(a.subject);
          });
        }
        break;
      case 'REASON':
        if (isProInputReasonAscending === false) {
          foundProInputs2.sort((a, b) => {
            return a.reason.localeCompare(b.reason);
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.reason.localeCompare(a.reason);
          });
        }
        break;
      case 'CONDITION1':
        if (isProInputCondition1Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition1
              .replaceAll(',', '')
              .localeCompare(b.condition1.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition1
              .replaceAll(',', '')
              .localeCompare(a.condition1.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION2':
        if (isProInputCondition2Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition2
              .replaceAll(',', '')
              .localeCompare(b.condition2.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition2
              .replaceAll(',', '')
              .localeCompare(a.condition2.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION3':
        if (isProInputCondition3Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition3

              .replaceAll(',', '')
              .localeCompare(b.condition3.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition3
              .replaceAll(',', '')
              .localeCompare(a.condition3.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION4':
        if (isProInputCondition4Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition4
              .replaceAll(',', '')
              .localeCompare(b.condition4.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition4
              .replaceAll(',', '')
              .localeCompare(a.condition4.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION5':
        if (isProInputCondition5Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition5
              .replaceAll(',', '')
              .localeCompare(b.condition5.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition5
              .replaceAll(',', '')
              .localeCompare(a.condition5.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION6':
        if (isProInputCondition6Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition6
              .replaceAll(',', '')
              .localeCompare(b.condition6.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition6
              .replaceAll(',', '')
              .localeCompare(a.condition6.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION7':
        if (isProInputCondition7Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition7
              .replaceAll(',', '')
              .localeCompare(b.condition7.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition7
              .replaceAll(',', '')
              .localeCompare(a.condition7.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION8':
        if (isProInputCondition8Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition8
              .replaceAll(',', '')
              .localeCompare(b.condition8.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition8
              .replaceAll(',', '')
              .localeCompare(a.condition8.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION9':
        if (isProInputCondition9Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition9
              .replaceAll(',', '')
              .localeCompare(b.condition9.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition9
              .replaceAll(',', '')
              .localeCompare(a.condition9.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION10':
        if (isProInputCondition10Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition10
              .replaceAll(',', '')
              .localeCompare(b.condition10.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition10
              .replaceAll(',', '')
              .localeCompare(a.condition10.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION11':
        if (isProInputCondition11Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition11
              .replaceAll(',', '')
              .localeCompare(b.condition11.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition11
              .replaceAll(',', '')
              .localeCompare(a.condition11.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION12':
        if (isProInputCondition12Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition12
              .replaceAll(',', '')
              .localeCompare(b.condition12.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition12
              .replaceAll(',', '')
              .localeCompare(a.condition12.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION13':
        if (isProInputCondition13Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition13
              .replaceAll(',', '')
              .localeCompare(b.condition13.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition13
              .replaceAll(',', '')
              .localeCompare(a.condition13.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION14':
        if (isProInputCondition14Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition14
              .replaceAll(',', '')
              .localeCompare(b.condition14.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition14
              .replaceAll(',', '')
              .localeCompare(a.condition14.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION15':
        if (isProInputCondition15Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition15
              .replaceAll(',', '')
              .localeCompare(b.condition15.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition15
              .replaceAll(',', '')
              .localeCompare(a.condition15.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION16':
        if (isProInputCondition16Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition16
              .replaceAll(',', '')
              .localeCompare(b.condition16.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition16
              .replaceAll(',', '')
              .localeCompare(a.condition16.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION17':
        if (isProInputCondition17Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition17
              .replaceAll(',', '')
              .localeCompare(b.condition17.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition17
              .replaceAll(',', '')
              .localeCompare(a.condition17.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION18':
        if (isProInputCondition18Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition18
              .replaceAll(',', '')
              .localeCompare(b.condition18.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition18
              .replaceAll(',', '')
              .localeCompare(a.condition18.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION19':
        if (isProInputCondition19Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition19
              .replaceAll(',', '')
              .localeCompare(b.condition19.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition19
              .replaceAll(',', '')
              .localeCompare(a.condition19.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION20':
        if (isProInputCondition20Ascending === false) {
          foundProInputs2.sort((a, b) => {
            return a.condition20
              .replaceAll(',', '')
              .localeCompare(b.condition20.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.condition20
              .replaceAll(',', '')
              .localeCompare(a.condition20.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'DATA':
        if (isProInputDataAscending === false) {
          foundProInputs2.sort((a, b) => {
            return a.data
              .replaceAll(',', '')
              .localeCompare(b.data.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.data
              .replaceAll(',', '')
              .localeCompare(a.data.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'UNIT':
        if (isProInputUnitAscending === false) {
          foundProInputs2.sort((a, b) => {
            return a.unit.localeCompare(b.unit);
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b.unit.localeCompare(a.unit);
          });
        }
        break;
      default:
        if (isProInputIdAscending === false) {
          foundProInputs2.sort((a, b) => {
            return a._id.localeCompare(b._id);
          });
        } else {
          foundProInputs2.sort((a, b) => {
            return b._id.localeCompare(a._id);
          });
        }
    }

    function onlyUnique (value, index, array) {
      return array.indexOf(value) === index;
    }
    foundProInputs2 = foundProInputs2.filter(onlyUnique);

    // Show newest-first, then float pinned items to the very top regardless
    // of whatever column/direction is currently sorted (unless pinning is
    // switched off, in which case just show the plain sort order)
    foundProInputs2 = pinEnabled
      ? orderWithPinnedFirst(foundProInputs2.reverse())
      : foundProInputs2.reverse();

    const csvData = foundProInputs2;

    // Extract keywords for highlighting
    const searchKeywords = extractSearchKeywords(props.keyword);

    // Page number each item currently lands on (list is already in final display order)
    const pageById = {};
    foundProInputs2.forEach((inp, idx) => {
      pageById[inp._id] = Math.floor(idx / proInputListPerPage) + 1;
    });

    // Deep linking: jump to wherever the linked item currently lives, computed
    // fresh from the live list above rather than a page number stored in the
    // URL (which would go stale as new records shift everything's position).
    const deepLinkItemId = searchParams.get('id');
    if (deepLinkItemId && pageById[deepLinkItemId] !== undefined) {
      pendingDeepLinkPageRef.current = { id: deepLinkItemId, page: pageById[deepLinkItemId] };
    }

    const proInputList = foundProInputs2.map(proInput => {
      return (
        <Fragment key={proInput._id}>
          <RenderProInputTable
            proInput={proInput}
            deleteProInput={props.deleteProInput}
            updateProInput={props.updateProInput}
            updateProInputFields={props.updateProInputFields}
            resetProInputForm={props.resetProInputForm}
            auth={props.auth}
            updatingId={props.updatingId}
            isFullContentShown={isFullContentShown}
            toggleIsFullContentShown={toggleIsFullContentShown}
            saveScrollPosition={props.saveScrollPosition} // Pass saveScrollPosition
            restoreScrollPosition={props.restoreScrollPosition} // Pass restoreScrollPosition
            searchKeywords={searchKeywords}
            keyword={props.keyword}
            view={props.view}
            getColumnWidth={getColumnWidth}
            startResize={startResize}
            htmlResumeRevision={props.htmlResumeRevision}
          />
          {/* </div> */}
        </Fragment>
      );
    });

    proInput_num = proInputList.length;

    // Get current posts
    const indexOfLastProInputList = currentPage * proInputListPerPage;
    const indexOfFirstProInputList =
      indexOfLastProInputList - proInputListPerPage;
    const currentProInputList = proInputList
      .slice(indexOfFirstProInputList, indexOfLastProInputList);

    return (
      <div ref={containerRef}>
        <Card>
          <Row className='form-group'>
            <Col>
              <CardBody>
                <div className='row'>
                  <div className='col-6'>
                    {/* <h5>{proInput_num} items</h5> */}
                    <Button
                      outline
                      color={
                        isProInputIdAscending === false
                          ? 'primary'
                          : 'secondary'
                      }
                      // color='secondary'
                      onClick={toggleIsProInputIdAscending}
                      // style={{ border: 'none' }}
                    >
                      {proInput_num} items
                    </Button>
                    <Button
                      outline
                      onClick={toggleIsFullContentShown}
                      style={{ marginLeft: '10px' }}
                    >
                      {isFullContentShown ? '...' : 'Show all'}
                    </Button>
                    <Button
                      outline
                      size='sm'
                      color={manualResizeEnabled ? 'primary' : 'secondary'}
                      onClick={() => setManualResizeEnabled(!manualResizeEnabled)}
                      style={{ marginLeft: '10px' }}
                      title={manualResizeEnabled ? 'Manual column widths are on: drag a column border to resize it. Click to turn off and auto-size again.' : 'Manual column widths are off: columns auto-size to their content. Click to turn on and drag column borders to resize.'}
                    >
                      <span className='fa fa-arrows-h'></span>
                    </Button>
                  </div>
                  <div className='col-6' style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
                    <CSVLink
                      data={csvData}
                      className='fa fa-arrow-down'
                    ></CSVLink>
                    <Button
                      outline
                      size='sm'
                      color={pinEnabled ? 'primary' : 'secondary'}
                      onClick={() => setPinEnabled(!pinEnabled)}
                      title={pinEnabled ? 'Pinning is on: pinned items float to the top. Click to turn off.' : 'Pinning is off: pinned items are ignored. Click to turn on.'}
                    >
                      <span className='fa fa-thumb-tack'></span>
                    </Button>
                  </div>
                </div>
                <div
                  className='table-responsive'
                  ref={props.scrollContainerRef} // Attach the ref to the Card
                  onScroll={props.saveScrollPosition} // Attach the onScroll event
                  style={{ overflowX: 'auto', whiteSpace: 'nowrap' }} // Ensure scrolling is enabled
                >
                  <Table
                    bordered
                    hover
                    striped
                    style={{
                      wordWrap: 'break-word', // Enable word wrapping
                      whiteSpace: 'normal' // Allow content to wrap
                    }}
                  >
                    <thead>
                      <tr>
                        <th></th> {/* for Edit */}
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsProInputDateAscending}
                          >
                            date
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsProInputPlaceAscending}
                          >
                            place
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsProInputPersonAscending}
                          >
                            person
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsProInputSubjectAscending}
                          >
                            subject
                          </Button>
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('reason') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputReasonAscending}
                            >
                              reason
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('reason')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition1') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition1Ascending}
                            >
                              c1
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('reason')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition1')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition2') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition2Ascending}
                            >
                              c2
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition1')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition2')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition3') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition3Ascending}
                            >
                              c3
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition2')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition3')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition4') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition4Ascending}
                            >
                              c4
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition3')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition4')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition5') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition5Ascending}
                            >
                              c5
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition4')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition5')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition6') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition6Ascending}
                            >
                              c6
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition5')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition6')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition7') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition7Ascending}
                            >
                              c7
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition6')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition7')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition8') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition8Ascending}
                            >
                              c8
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition7')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition8')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition9') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition9Ascending}
                            >
                              c9
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition8')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition9')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition10') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition10Ascending}
                            >
                              c10
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition9')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition10')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition11') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition11Ascending}
                            >
                              c11
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition10')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition11')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition12') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition12Ascending}
                            >
                              c12
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition11')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition12')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition13') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition13Ascending}
                            >
                              c13
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition12')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition13')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition14') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition14Ascending}
                            >
                              c14
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition13')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition14')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition15') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition15Ascending}
                            >
                              c15
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition14')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition15')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition16') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition16Ascending}
                            >
                              c16
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition15')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition16')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition17') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition17Ascending}
                            >
                              c17
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition16')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition17')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition18') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition18Ascending}
                            >
                              c18
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition17')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition18')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition19') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition19Ascending}
                            >
                              c19
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition18')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition19')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition20') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition20Ascending}
                            >
                              c20
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition19')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition20')} />
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsProInputDataAscending}
                          >
                            data
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsProInputUnitAscending}
                          >
                            unit
                          </Button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>{currentProInputList}</tbody>
                  </Table>
                  <div className='text-center text-muted py-2'>
                    <small>Ver {props.version} © 2022-2026 Cage H.</small>
                  </div>
                </div>
              </CardBody>
            </Col>
          </Row>
        </Card>
        <Pagination
          inputListPerPage={proInputListPerPage}
          totalInputList={proInputList.length}
          paginate={paginate}
          currentPage={currentPage}
        />
        {/* Scroll toggle button */}
        <button
          style={scrollButtonStyle}
          onClick={toggleScrollPosition}
          aria-label={isAtTop && savedScrollPosition !== null ? 'Go back' : 'Scroll to top'}
        >
          <span className={isAtTop && savedScrollPosition !== null ? 'fa fa-arrow-down' : 'fa fa-arrow-up'}></span>
        </button>
      </div>
    );
  } else if (props.proInputs !== null) {
    // Create a sorted copy first (same pattern as Data spreadsheet view)
    let sortedProInputs = [...props.proInputs];

    const conv = proInputDate => {
      let day = proInputDate.split(' ').find(element => element.includes('-'));
      if (day) {
        const len = day.split('-')[0].length;

        if (!day.split('-')[0]) {
          // console.error(`Unable to parse date from "${inputDate}"`);
          return '';
        }

        if (!day.split('-')[1]) {
          // console.error(`Unable to parse date from "${inputDate}"`);
          return '';
        }

        if (!day.split('-')[2]) {
          // console.error(`Unable to parse date from "${inputDate}"`);
          return '';
        }

        const firstnumber = day.split('-')[0].padStart(2, '0');
        const secondnumber = day.split('-')[1].padStart(2, '0');
        const thirdnumber = day.split('-')[2].padStart(2, '0');

        let time = proInputDate
          .split(' ')
          .find(element => element.includes(':'));
        if (!time) {
          if (len === 1 || len === 2) {
            day = `${secondnumber}-${thirdnumber}-${firstnumber} 00:00:00`;
            // console.log('day + time is', `${day} ${time}`);
          } else {
            day = `${firstnumber}-${secondnumber}-${thirdnumber} 00:00:00`;
            // console.log('day + time is', `${day} ${time}`);
          }
        } else {
          const [hour, minute] = time
            .split(':')
            .map(num => num.padStart(2, '0'));
          if (len === 1 || len === 2) {
            day = `${secondnumber}-${thirdnumber}-${firstnumber} ${hour}:${minute}:00`;
            // console.log('day + time is', `${day} ${time}`);
          } else {
            day = `${firstnumber}-${secondnumber}-${thirdnumber} ${hour}:${minute}:00`;
            // console.log('day + time is', `${day} ${time}`);
          }
        }
      }

      // const time = proInputDate
      //   .split(' ')
      //   .find(element => element.includes(':'));

      if (!day) {
        // console.error(`Unable to parse date from "${inputDate}"`);
        return '';
      }

      return day;
    };

    switch (toggledProInputFieldButton) {
      case 'ID':
        if (isProInputIdAscending === false) {
          sortedProInputs.sort((a, b) => {
            return a._id.localeCompare(b._id);
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b._id.localeCompare(a._id);
          });
        }
        break;

      case 'DATE':
        if (isProInputDateAscending === false) {
          sortedProInputs.sort((a, b) => {
            // return a.date.localeCompare(b.date);
            return conv(a.date).localeCompare(conv(b.date));
            // return new Date(conv(a.date)) - new Date(conv(b.date));
          });
        } else {
          sortedProInputs.sort((a, b) => {
            // return b.date.localeCompare(a.date);
            return conv(b.date).localeCompare(conv(a.date));
            // return new Date(conv(b.date)) - new Date(conv(a.date));
          });
        }

        break;
      case 'PLACE':
        if (isProInputPlaceAscending === false) {
          sortedProInputs.sort((a, b) => {
            return a.place.localeCompare(b.place);
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.place.localeCompare(a.place);
          });
        }
        break;
      case 'PERSON':
        if (isProInputPersonAscending === false) {
          sortedProInputs.sort((a, b) => {
            return a.person.localeCompare(b.person);
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.person.localeCompare(a.person);
          });
        }
        break;
      case 'SUBJECT':
        if (isProInputSubjectAscending === false) {
          sortedProInputs.sort((a, b) => {
            return a.subject.localeCompare(b.subject);
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.subject.localeCompare(a.subject);
          });
        }
        break;
      case 'REASON':
        if (isProInputReasonAscending === false) {
          sortedProInputs.sort((a, b) => {
            return a.reason.localeCompare(b.reason);
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.reason.localeCompare(a.reason);
          });
        }
        break;
      case 'CONDITION1':
        if (isProInputCondition1Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition1
              .replaceAll(',', '')
              .localeCompare(b.condition1.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition1
              .replaceAll(',', '')
              .localeCompare(a.condition1.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION2':
        if (isProInputCondition2Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition2
              .replaceAll(',', '')
              .localeCompare(b.condition2.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition2
              .replaceAll(',', '')
              .localeCompare(a.condition2.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION3':
        if (isProInputCondition3Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition3

              .replaceAll(',', '')
              .localeCompare(b.condition3.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition3
              .replaceAll(',', '')
              .localeCompare(a.condition3.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION4':
        if (isProInputCondition4Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition4
              .replaceAll(',', '')
              .localeCompare(b.condition4.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition4
              .replaceAll(',', '')
              .localeCompare(a.condition4.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION5':
        if (isProInputCondition5Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition5
              .replaceAll(',', '')
              .localeCompare(b.condition5.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition5
              .replaceAll(',', '')
              .localeCompare(a.condition5.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION6':
        if (isProInputCondition6Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition6
              .replaceAll(',', '')
              .localeCompare(b.condition6.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition6
              .replaceAll(',', '')
              .localeCompare(a.condition6.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION7':
        if (isProInputCondition7Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition7
              .replaceAll(',', '')
              .localeCompare(b.condition7.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition7
              .replaceAll(',', '')
              .localeCompare(a.condition7.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION8':
        if (isProInputCondition8Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition8
              .replaceAll(',', '')
              .localeCompare(b.condition8.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition8
              .replaceAll(',', '')
              .localeCompare(a.condition8.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION9':
        if (isProInputCondition9Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition9
              .replaceAll(',', '')
              .localeCompare(b.condition9.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition9
              .replaceAll(',', '')
              .localeCompare(a.condition9.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION10':
        if (isProInputCondition10Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition10
              .replaceAll(',', '')
              .localeCompare(b.condition10.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition10
              .replaceAll(',', '')
              .localeCompare(a.condition10.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION11':
        if (isProInputCondition11Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition11
              .replaceAll(',', '')
              .localeCompare(b.condition11.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition11
              .replaceAll(',', '')
              .localeCompare(a.condition11.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION12':
        if (isProInputCondition12Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition12
              .replaceAll(',', '')
              .localeCompare(b.condition12.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition12
              .replaceAll(',', '')
              .localeCompare(a.condition12.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION13':
        if (isProInputCondition13Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition13
              .replaceAll(',', '')
              .localeCompare(b.condition13.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition13
              .replaceAll(',', '')
              .localeCompare(a.condition13.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION14':
        if (isProInputCondition14Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition14
              .replaceAll(',', '')
              .localeCompare(b.condition14.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition14
              .replaceAll(',', '')
              .localeCompare(a.condition14.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION15':
        if (isProInputCondition15Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition15
              .replaceAll(',', '')
              .localeCompare(b.condition15.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition15
              .replaceAll(',', '')
              .localeCompare(a.condition15.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION16':
        if (isProInputCondition16Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition16
              .replaceAll(',', '')
              .localeCompare(b.condition16.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition16
              .replaceAll(',', '')
              .localeCompare(a.condition16.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION17':
        if (isProInputCondition17Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition17
              .replaceAll(',', '')
              .localeCompare(b.condition17.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition17
              .replaceAll(',', '')
              .localeCompare(a.condition17.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION18':
        if (isProInputCondition18Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition18
              .replaceAll(',', '')
              .localeCompare(b.condition18.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition18
              .replaceAll(',', '')
              .localeCompare(a.condition18.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION19':
        if (isProInputCondition19Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition19
              .replaceAll(',', '')
              .localeCompare(b.condition19.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition19
              .replaceAll(',', '')
              .localeCompare(a.condition19.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'CONDITION20':
        if (isProInputCondition20Ascending === false) {
          sortedProInputs.sort((a, b) => {
            return a.condition20
              .replaceAll(',', '')
              .localeCompare(b.condition20.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.condition20
              .replaceAll(',', '')
              .localeCompare(a.condition20.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'DATA':
        if (isProInputDataAscending === false) {
          sortedProInputs.sort((a, b) => {
            return a.data
              .replaceAll(',', '')
              .localeCompare(b.data.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.data
              .replaceAll(',', '')
              .localeCompare(a.data.replaceAll(',', ''), undefined, {
                numeric: true
              });
          });
        }
        break;
      case 'UNIT':
        if (isProInputUnitAscending === false) {
          sortedProInputs.sort((a, b) => {
            return a.unit.localeCompare(b.unit);
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b.unit.localeCompare(a.unit);
          });
        }
        break;
      default:
        if (isProInputIdAscending === false) {
          sortedProInputs.sort((a, b) => {
            return a._id.localeCompare(b._id);
          });
        } else {
          sortedProInputs.sort((a, b) => {
            return b._id.localeCompare(a._id);
          });
        }
    }

    // Show newest-first, then float pinned items to the very top regardless
    // of whatever column/direction is currently sorted (unless pinning is
    // switched off, in which case just show the plain sort order)
    sortedProInputs = pinEnabled
      ? orderWithPinnedFirst(sortedProInputs.reverse())
      : sortedProInputs.reverse();

    // Map AFTER sorting (same pattern as Data spreadsheet view)
    const pageById = {};
    sortedProInputs.forEach((inp, idx) => {
      pageById[inp._id] = Math.floor(idx / proInputListPerPage) + 1;
    });

    // Deep linking: jump to wherever the linked item currently lives, computed
    // fresh from the live list above rather than a page number stored in the
    // URL (which would go stale as new records shift everything's position).
    const deepLinkItemId = searchParams.get('id');
    if (deepLinkItemId && pageById[deepLinkItemId] !== undefined) {
      pendingDeepLinkPageRef.current = { id: deepLinkItemId, page: pageById[deepLinkItemId] };
    }

    const proInputList = sortedProInputs.map(proInput => {
      return (
        <Fragment key={proInput._id}>
          <RenderProInputTable
            proInput={proInput}
            deleteProInput={props.deleteProInput}
            updateProInput={props.updateProInput}
            updateProInputFields={props.updateProInputFields}
            resetProInputForm={props.resetProInputForm}
            auth={props.auth}
            updatingId={props.updatingId}
            isFullContentShown={isFullContentShown}
            toggleIsFullContentShown={toggleIsFullContentShown}
            saveScrollPosition={props.saveScrollPosition}
            restoreScrollPosition={props.restoreScrollPosition}
            searchKeywords={[]}
            keyword={props.keyword}
            view={props.view}
            getColumnWidth={getColumnWidth}
            startResize={startResize}
            htmlResumeRevision={props.htmlResumeRevision}
          />
        </Fragment>
      );
    });

    proInput_num = proInputList.length;
    const csvData = sortedProInputs;

    // Get current posts
    const indexOfLastProInputList = currentPage * proInputListPerPage;
    const indexOfFirstProInputList =
      indexOfLastProInputList - proInputListPerPage;
    const currentProInputList = proInputList
      .slice(indexOfFirstProInputList, indexOfLastProInputList);

    return (
      <div ref={containerRef}>
        <Card>
          <Row className='form-group'>
            <Col>
              <CardBody>
                <div className='row'>
                  <div className='col-6'>
                    {/* <h5>{proInput_num} items</h5> */}
                    <Button
                      outline
                      color={
                        isProInputIdAscending === false
                          ? 'primary'
                          : 'secondary'
                      }
                      // color='secondary'
                      onClick={toggleIsProInputIdAscending}
                      // style={{ border: 'none' }}
                    >
                      {proInput_num} items
                    </Button>
                    <Button
                      outline
                      onClick={toggleIsFullContentShown}
                      style={{ marginLeft: '10px' }}
                    >
                      {isFullContentShown ? '...' : 'Show all'}
                    </Button>
                    <Button
                      outline
                      size='sm'
                      color={manualResizeEnabled ? 'primary' : 'secondary'}
                      onClick={() => setManualResizeEnabled(!manualResizeEnabled)}
                      style={{ marginLeft: '10px' }}
                      title={manualResizeEnabled ? 'Manual column widths are on: drag a column border to resize it. Click to turn off and auto-size again.' : 'Manual column widths are off: columns auto-size to their content. Click to turn on and drag column borders to resize.'}
                    >
                      <span className='fa fa-arrows-h'></span>
                    </Button>
                  </div>
                  <div className='col-6' style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
                    <CSVLink
                      data={csvData}
                      className='fa fa-arrow-down'
                    ></CSVLink>
                    <Button
                      outline
                      size='sm'
                      color={pinEnabled ? 'primary' : 'secondary'}
                      onClick={() => setPinEnabled(!pinEnabled)}
                      title={pinEnabled ? 'Pinning is on: pinned items float to the top. Click to turn off.' : 'Pinning is off: pinned items are ignored. Click to turn on.'}
                    >
                      <span className='fa fa-thumb-tack'></span>
                    </Button>
                  </div>
                </div>
                <div
                  className='table-responsive'
                  ref={props.scrollContainerRef} // Attach the ref to the Card
                  onScroll={props.saveScrollPosition} // Attach the onScroll event
                  style={{ overflowX: 'auto', whiteSpace: 'nowrap' }} // Ensure scrolling is enabled
                >
                  <Table
                    bordered
                    hover
                    striped
                    className='table'
                    style={{
                      wordWrap: 'break-word', // Enable word wrapping
                      whiteSpace: 'normal' // Allow content to wrap
                    }}
                  >
                    <thead>
                      <tr>
                        <th></th> {/* for Edit */}
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsProInputDateAscending}
                          >
                            date
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsProInputPlaceAscending}
                          >
                            place
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsProInputPersonAscending}
                          >
                            person
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsProInputSubjectAscending}
                          >
                            subject
                          </Button>
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('reason') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputReasonAscending}
                            >
                              reason
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('reason')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition1') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition1Ascending}
                            >
                              c1
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('reason')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition1')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition2') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition2Ascending}
                            >
                              c2
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition1')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition2')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition3') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition3Ascending}
                            >
                              c3
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition2')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition3')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition4') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition4Ascending}
                            >
                              c4
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition3')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition4')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition5') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition5Ascending}
                            >
                              c5
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition4')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition5')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition6') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition6Ascending}
                            >
                              c6
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition5')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition6')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition7') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition7Ascending}
                            >
                              c7
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition6')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition7')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition8') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition8Ascending}
                            >
                              c8
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition7')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition8')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition9') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition9Ascending}
                            >
                              c9
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition8')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition9')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition10') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition10Ascending}
                            >
                              c10
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition9')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition10')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition11') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition11Ascending}
                            >
                              c11
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition10')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition11')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition12') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition12Ascending}
                            >
                              c12
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition11')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition12')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition13') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition13Ascending}
                            >
                              c13
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition12')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition13')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition14') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition14Ascending}
                            >
                              c14
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition13')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition14')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition15') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition15Ascending}
                            >
                              c15
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition14')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition15')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition16') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition16Ascending}
                            >
                              c16
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition15')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition16')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition17') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition17Ascending}
                            >
                              c17
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition16')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition17')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition18') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition18Ascending}
                            >
                              c18
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition17')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition18')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition19') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition19Ascending}
                            >
                              c19
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition18')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition19')} />
                        </th>
                        <th style={{ position: 'relative' }}>
                          <div style={{ width: getColumnWidth('condition20') }}>
                            <Button
                              outline
                              color='secondary'
                              onClick={toggleIsProInputCondition20Ascending}
                            >
                              c20
                            </Button>
                          </div>
                          <ColumnResizeHandle onMouseDown={startResize('condition19')} side='left' />

                          <ColumnResizeHandle onMouseDown={startResize('condition20')} />
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsProInputDataAscending}
                          >
                            data
                          </Button>
                        </th>
                        <th>
                          <Button
                            outline
                            color='secondary'
                            onClick={toggleIsProInputUnitAscending}
                          >
                            unit
                          </Button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>{currentProInputList}</tbody>
                  </Table>
                  <div className='text-center text-muted py-2'>
                    <small>Ver {props.version} © 2022-2026 Cage H.</small>
                  </div>
                </div>
              </CardBody>
            </Col>
          </Row>
        </Card>
        <Pagination
          inputListPerPage={proInputListPerPage}
          totalInputList={proInputList.length}
          paginate={paginate}
          currentPage={currentPage}
        />
        {/* Scroll toggle button */}
        <button
          style={scrollButtonStyle}
          onClick={toggleScrollPosition}
          aria-label={isAtTop && savedScrollPosition !== null ? 'Go back' : 'Scroll to top'}
        >
          <span className={isAtTop && savedScrollPosition !== null ? 'fa fa-arrow-down' : 'fa fa-arrow-up'}></span>
        </button>
      </div>
    );
  } else {
    return <div></div>;
  }
}
